import type { Catalogue } from './en';

/**
 * ar.ts — every string the console says, in Arabic.
 *
 * ### THIS TRANSLATION HAS NOT BEEN REVIEWED BY A NATIVE SPEAKER
 *
 * It was drafted so that the Arabic console is testable end to end — RTL
 * verification (H4/S6) needs real text to render, and a catalogue of English
 * placeholders proves nothing about how the layout behaves. It is not a
 * substitute for review, and the terms below are where a reviewer should look
 * hardest, because a wrong word here is a domain error rather than a typo:
 *
 * | English | Used here | Why it is contestable |
 * |---|---|---|
 * | Persona | الشخصية النمطية | A candidate segment, not a person. "الشريحة" may read better to ETCC. |
 * | Scoring | التقدير | التصحيح is marking a right answer; these instruments have no right answers. |
 * | Item / Question | السؤال | The bank calls them items; Arabic assessment practice often says الفقرة. |
 * | Certified | معتمد | Approved-for-use, not "accredited by a body". |
 * | Band | النطاق | A score range with a label. |
 * | Blueprint | مخطط التقييم | The published persona × module plan. |
 * | Discrimination | التمييز | IRT parameter, not the everyday sense of the word. |
 *
 * `Catalogue` requires every key `en.ts` defines, so this file cannot fall
 * behind the English one without failing the build. It may carry EXTRA keys, and
 * only for one reason: Arabic has six plural categories where English has two.
 */
export const ar: Catalogue = {
  /* ------------------------------------------------------------- vocabulary */
  'common.save': 'حفظ التغييرات',
  'common.saving': 'جارٍ الحفظ…',
  'common.cancel': 'إلغاء',
  'common.close': 'إغلاق',
  'common.create': 'إنشاء',
  'common.edit': 'تعديل',
  'common.delete': 'حذف',
  'common.retry': 'إعادة المحاولة',
  'common.back': 'رجوع',
  'common.next': 'التالي',
  'common.previous': 'السابق',
  'common.confirm': 'تأكيد',
  'common.search': 'بحث',
  'common.filter': 'تصفية',
  'common.clear': 'مسح',
  'common.all': 'الكل',
  'common.none': 'لا شيء',
  'common.loading': 'جارٍ التحميل…',
  'common.copy': 'نسخ',
  'common.copied': 'تم النسخ',
  'common.export': 'تصدير',
  'common.import': 'استيراد',
  'common.download': 'تنزيل',
  'common.refresh': 'تحديث',
  'common.view': 'عرض',
  'common.open': 'فتح',
  'common.done': 'تم',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.required': 'مطلوب',
  'common.optional': 'اختياري',
  'common.unknown': 'غير معروف',
  'common.notAvailable': '—',
  'common.english': 'الإنجليزية',
  'common.arabic': 'العربية',
  'common.minutes': '{minutes} دقيقة',
  'common.moreInfo': 'مزيد من المعلومات',

  /* ------------------------------------------------------------------ table */
  'table.errorTitle': 'تعذّر تحميل هذه القائمة',
  'table.errorDescription': 'فشل الطلب.',
  'table.page': 'صفحة {page} من {pages} · {total} إجمالاً',

  /* ------------------------------------------------------------------- nav */
  'nav.toggleSidebar': 'إظهار القائمة الجانبية أو إخفاؤها',
  'nav.group.overview': 'نظرة عامة',
  'nav.group.design': 'تصميم التقييم',
  'nav.group.delivery': 'التقديم',
  'nav.group.platform': 'المنصة',
  'nav.dashboard': 'لوحة المعلومات',
  'nav.analytics': 'التحليلات',
  'nav.modules': 'الوحدات',
  'nav.questions': 'الأسئلة',
  'nav.questions.all': 'كل الأسئلة',
  'nav.questions.types': 'أنواع الأسئلة',
  'nav.scoring': 'محرك التقدير',
  'nav.personas': 'الشخصيات النمطية والمصفوفة',
  'nav.candidates': 'المرشحون',
  'nav.qcReview': 'مراجعة الجودة',
  // ستّ صيغ، لأن العربية تحتاجها؛ الصيغتان one وtwo تحملان العدد في اللفظ.
  'nav.qcReview.held.zero': 'لا نتائج محجوزة للمراجعة',
  'nav.qcReview.held.one': 'نتيجة واحدة محجوزة للمراجعة',
  'nav.qcReview.held.two': 'نتيجتان محجوزتان للمراجعة',
  'nav.qcReview.held.few': '{count} نتائج محجوزة للمراجعة',
  'nav.qcReview.held.many': '{count} نتيجة محجوزة للمراجعة',
  'nav.qcReview.held.other': '{count} نتيجة محجوزة للمراجعة',
  'nav.take': 'تشغيل تجريبي (ضبط الجودة)',
  'nav.notifications': 'الإشعارات',
  'nav.support': 'الدعم',
  'nav.settings': 'الإعدادات',
  'nav.crumb.designQuestions': 'تصميم التقييم · الأسئلة',

  /* ---------------------------------------------------------------- chrome */
  'chrome.brand': 'تقييمات نافس',
  'chrome.brandSub': 'إدارة ETCC',
  'chrome.searchPlaceholder': 'ابحث في الأسئلة والمرشحين والوحدات…',
  'chrome.copyright': '© نافس · ETCC 2026',
  'chrome.residency': 'استضافة البيانات في الإمارات',
  'chrome.openProfile': 'فتح الملف الشخصي',
  'chrome.signOut': 'تسجيل الخروج',
  'chrome.environmentTitle': 'هذه الواجهة متصلة بواجهة برمجة التطبيقات لبيئة {environment}',
  'chrome.themeToLight': 'التبديل إلى الوضع الفاتح',
  'chrome.themeToDark': 'التبديل إلى الوضع الداكن',
  'chrome.notifications': 'الإشعارات',
  'chrome.notificationsUnread': 'الإشعارات ({count} غير مقروء)',
  'chrome.notificationsEmpty':
    'لا شيء لعرضه. ستصل الإشعارات بمجرد أن تتمكّن المنصة من نشرها — فواجهة القراءة على سجل التغييرات لم تُبنَ بعد.',
  'chrome.viewAll': 'عرض الكل',
  'chrome.accept': 'قبول',
  'chrome.decline': 'رفض',
  'chrome.accepted.title': 'تم قبول طلب الوصول',
  'chrome.declined.title': 'تم رفض طلب الوصول',

  /* ---------------------------------------------------- the topbar search */
  'search.results': 'نتائج البحث',
  'search.searching': 'جارٍ البحث…',
  'search.error': 'تعذّر تنفيذ هذا البحث',
  'search.empty': 'لا شيء يطابق «{query}»',
  'search.scope':
    'يبحث هذا الحقل في نصوص الأسئلة وأسماء الوحدات ومعرّفات المرشّحين. أما الشرائح والنتائج والإعدادات فلا يمكن البحث فيها من هنا بعد.',
  'search.group.questions': 'الأسئلة',
  'search.group.modules': 'الوحدات',
  'search.group.candidates': 'المرشّحون',
  'search.seeAll.questions': 'عرض كل الأسئلة المطابقة لـ «{query}»',
  'search.seeAll.modules': 'عرض كل الوحدات المطابقة لـ «{query}»',
  'search.seeAll.candidates': 'عرض كل المرشّحين المطابقين لـ «{query}»',

  /* ------------------------------------------------------------------ roles */
  // "مسؤول" alone is an adjective — "responsible" — and reads as a fragment
  // where a job title belongs. "مسؤول النظام" is the standard rendering of
  // Administrator in Arabic software UIs. ("مدير النظام" is the other accepted
  // form; it is one line to switch if ETCC prefers it.)
  'role.admin': 'مسؤول النظام',
  'role.analyst': 'محلل',
  'role.psychometrician': 'أخصائي قياس نفسي',

  /* ----------------------------------------------------------- error pages */
  'error.traceId': 'معرّف التتبع:',
  'error.backToDashboard': 'العودة إلى لوحة المعلومات',
  'error.403.title': 'ليس لديك صلاحية الوصول',
  'error.403.description':
    'دورك لا يسمح بالوصول إلى هذه الشاشة. تُطبَّق الصلاحيات على الخادم، لذا لا يمكن للواجهة منحها.',
  'error.404.title': 'الصفحة غير موجودة',
  'error.404.description':
    'هذا المسار غير موجود. ربما أُعيدت تسميته، أو أن الشاشة لم تُبنَ بعد.',
  'error.500.title': 'حدث خطأ ما',
  'error.500.description':
    'تعذّر على الخادم إتمام الطلب. إذا تكرّر ذلك، أرسل معرّف التتبع أدناه إلى الدعم.',

  /* ------------------------------------------------------------------ login */
  'login.brandSub': 'إدارة ETCC',
  'login.headline': 'تقييم نفسي مُركَّب لا مبرمَج',
  'login.blurb':
    'الوحدات بيانات، والنماذج النفسية إضافات برمجية. تُركِّب الشخصيات النمطية الوحدات في مخططات تقييم، وتُنشر المخططات كإصدارات خطط، وكل نسخة تقييم تُولَّد تثبّت الإصدارات التي بُنيت منها.',
  'login.footer': 'استضافة البيانات في الإمارات · ثنائي اللغة (EN / AR) · © نافس · ETCC 2026',
  'login.title': 'مرحبًا بعودتك',
  'login.subtitle': 'سجّل الدخول إلى واجهة إدارة نافس',
  'login.email': 'البريد الإلكتروني',
  'login.password': 'كلمة المرور',
  'login.forgot': 'تعذّر الدخول؟ يُصدر المسؤول حسابًا جديدًا.',
  'login.signIn': 'تسجيل الدخول',
  'login.signingIn': 'جارٍ تسجيل الدخول…',
  'login.unreachable': 'تعذّر الوصول إلى خدمة تسجيل الدخول.',
  'login.ssoNote': 'يُحدَّد مزوّد الهوية الذي يوثّق المشغّلين لكل بيئة على حدة.',

  /* ---------------------------------------------------------------- profile */
  'profile.title': 'الملف الشخصي',
  'profile.description': 'حسابك وتفضيلات الواجهة.',
  'profile.tab.account': 'الحساب',
  'profile.tab.appearance': 'المظهر',
  'profile.account.title': 'بيانات الحساب',
  'profile.account.description': 'هويتك كما تحفظها المنصة.',
  'profile.name': 'الاسم',
  'profile.role': 'الدور',
  'profile.notSignedIn': 'غير مسجّل الدخول',
  'profile.role.detail':
    'يُسنده مسؤول من الإعدادات ← مستخدمو الواجهة. وتُفحص الصلاحيات على الخادم، فما يمكنك فعله لا يتوقف على ما تعرضه هذه الشاشة.',
  'profile.language': 'لغة الواجهة',
  'profile.language.detail':
    'تغيّر هذه الواجهة فقط. أما المرشّح فيُقدَّم له التقييم باللغة المحدّدة عند توليده، ولا يمكن لهذا الإعداد التأثير فيها.',
  'profile.language.saved': 'تم تحديث لغة الواجهة',
  'profile.language.failed': 'تعذّر حفظ لغتك',
  'profile.theme.title': 'السمة',
  'profile.theme.description':
    'المظهر تفضيل خاص بالجهاز وليس إعدادًا للحساب، لذا يُضبط مرة واحدة للواجهة من الإعدادات.',
  'profile.theme.preset': 'السمة',
  'profile.theme.mode': 'فاتح أو داكن',
  'profile.theme.light': 'فاتح',
  'profile.theme.dark': 'داكن',
  'profile.theme.system': 'حسب النظام',
  'profile.theme.changeInSettings': 'التغيير من الإعدادات ← المظهر',

  /* ------------------------------------------------------------ closed enums */
  'state.ready': 'جاهز',
  'state.in_progress': 'قيد التنفيذ',
  'state.submitted': 'مُسلَّم',
  'state.scoring': 'قيد التقدير',
  'state.completed': 'مكتمل',
  'state.expired': 'منتهي الصلاحية',
  'state.flagged': 'مُعلَّم للمراجعة',
  'state.not_required': 'غير مطلوب',
  'state.pending': 'قيد الانتظار',

  'status.draft': 'مسودة',
  'status.review': 'قيد المراجعة',
  'status.certified': 'معتمد',
  'status.retired': 'مُوقَف',

  'moduleStatus.draft': 'مسودة',
  'moduleStatus.active': 'نشِط',
  'moduleStatus.retired': 'مُوقَف',

  'band.Low': 'منخفض',
  'band.Average': 'متوسط',
  'band.High': 'مرتفع',
  'skillBand.Emerging': 'ناشئ',
  'skillBand.Proficient': 'متمكّن',
  'skillBand.Advanced': 'متقدّم',

  'qcFlag.straight_lining': 'إجابات نمطية متكرّرة',
  'qcFlag.speeding': 'سرعة مفرطة في الإجابة',

  /* Dimensions. RIASEC's six Holland themes, the Big Five traits and the launch
     skill set. See the note in `en.ts`: the wire stays English by decision and
     the console translates. */
  'dimension.R': 'الواقعي',
  'dimension.I': 'الاستقصائي',
  'dimension.A': 'الفني',
  'dimension.S': 'الاجتماعي',
  'dimension.E': 'المبادر',
  'dimension.C': 'التقليدي',
  'dimension.openness': 'الانفتاح على الخبرات',
  'dimension.conscientiousness': 'يقظة الضمير',
  'dimension.extraversion': 'الانبساطية',
  'dimension.agreeableness': 'المقبولية',
  'dimension.emotionalStability': 'الاتزان الانفعالي',
  'dimension.data_analysis': 'تحليل البيانات',
  'dimension.problem_solving': 'حل المشكلات',
  'dimension.digital_literacy': 'المهارات الرقمية',

  /* جملة العنوان (N5). القيمة تصل من الخادم بالإنجليزية، والمفتاح والمتغيّرات هي
     ما يجعل ترجمتها ممكنة هنا: المتغيّرات مفاتيح لا نصوصًا، فيُبحث عن البُعد في
     `dimension.*` وعن النطاق في `band.*` قبل أن يدخلا الجملة. صيغت هذه الجمل
     لتُقرأ بالعربية، لا لتكون ترجمة حرفية للإنجليزية. */
  'headline.riasec.code_band': 'رمز هولاند {hollandCode} — {band}',
  'headline.big_five.highest_lowest': 'أعلى السمات {highest} وأدناها {lowest}',
  'headline.big_five.none': 'لم تُحتسب أي سمة',
  'headline.irt_bars.top_skill': 'أقوى المهارات {skill} — {band}',
  'headline.irt_bars.none': 'لم تُحتسب أي مهارة',

  /* -------------------------------------------------------------- dashboard */
  'dashboard.description':
    'عرض شامل للبرنامج عبر الوحدات الثلاث — كل رقم مقروء من تجميع النتائج.',
  'dashboard.filterByModule': 'تصفية حسب الوحدة',
  'dashboard.allModules': 'كل الوحدات',
  'dashboard.error': 'تعذّر تحميل لوحة المعلومات',
  'dashboard.empty.title': 'لا توجد تقييمات بعد',
  'dashboard.empty.description':
    'تمتلئ البطاقات من عمليات تقييم فعلية. ابدأ واحدة من التقديم ← إجراء تقييم.',
  'dashboard.completions': 'المكتملة',
  // Arabic distinguishes six plural categories; English needs two.
  'dashboard.scoredResults.zero': 'لا توجد نتائج مُقدَّرة',
  'dashboard.scoredResults.one': 'نتيجة مُقدَّرة واحدة',
  'dashboard.scoredResults.two': 'نتيجتان مُقدَّرتان',
  'dashboard.scoredResults.few': '{count} نتائج مُقدَّرة',
  'dashboard.scoredResults.many': '{count} نتيجة مُقدَّرة',
  'dashboard.scoredResults.other': '{count} نتيجة مُقدَّرة',
  'dashboard.inFlight.zero': 'لا توجد تقييمات جارية',
  'dashboard.inFlight.one': 'تقييم واحد جارٍ',
  'dashboard.inFlight.two': 'تقييمان جاريان',
  'dashboard.inFlight.few': '{count} تقييمات جارية',
  'dashboard.inFlight.many': '{count} تقييمًا جاريًا',
  'dashboard.inFlight.other': '{count} تقييم جارٍ',
  'dashboard.completionRate': 'معدّل الإكمال',
  'dashboard.completionRate.hint': 'المكتملة ÷ كل نسخة تقييم مُولَّدة',
  'dashboard.qualityFlags': 'مؤشرات الجودة',
  'dashboard.noFlags': 'لم تُرصد إجابات نمطية أو سرعة مفرطة',
  'dashboard.average': 'المتوسط',
  'dashboard.overTime': 'المكتملة عبر الزمن',
  'dashboard.overTime.hint': 'النتائج المُقدَّرة أسبوعيًا، آخر 26 أسبوعًا.',
  'dashboard.byDimension': 'متوسط الدرجة حسب البُعد',
  'dashboard.byDimension.hint':
    '{module} — أداة واحدة في كل مرة، لأن متوسطات الأبعاد من نماذج مختلفة مقاييس مختلفة.',
  'dashboard.noModule': 'لا توجد وحدة',
  'dashboard.noResultsForModule': 'لا توجد نتائج مُقدَّرة لهذه الوحدة بعد.',
  'dashboard.byModule': 'المكتملة حسب الوحدة',
  'dashboard.byModule.hint': 'حصة النتائج المُقدَّرة عبر الأدوات الثلاث.',
  'dashboard.viewMode': 'نمط العرض',
  'dashboard.viewDonut': 'رسم دائري',
  'dashboard.viewList': 'قائمة',
  'dashboard.noResults': 'لا توجد نتائج مُقدَّرة بعد.',
  'dashboard.column.module': 'الوحدة',
  'dashboard.column.state': 'الحالة',
  'dashboard.column.count': 'العدد',
  'dashboard.provenance':
    'مقروء من مسار $facet واحد على النتائج بالإضافة إلى $group واحد على نسخ التقييم — بلا $lookup في أيٍّ منهما، وهو ما يتحقّق منه اختبار على المسارات كما تُرسَل فعليًا.',

  /* ---------------------------------------------------------------- modules */
  'modules.title': 'وحدات التقييم',
  'modules.description': 'كل وحدة تربط إضافة تقدير مُسجَّلة ببنك أسئلة معتمد.',
  'modules.count.zero': 'لا توجد وحدات',
  'modules.count.one': 'وحدة واحدة',
  'modules.count.two': 'وحدتان',
  'modules.count.few': '{count} وحدات',
  'modules.count.many': '{count} وحدة',
  'modules.count.other': '{count} وحدة',
  'modules.new': 'وحدة جديدة',
  'modules.loadFailed': 'تعذّر تحميل الوحدات',
  'modules.empty.title': 'لا توجد وحدات بعد',
  'modules.empty.description':
    'تُعلن الوحدة نوع نموذجها، وهو ما يحدّد إضافة التقدير، وتتضمّن إعداد تقدير مُصدَّرًا بإصدار.',
  'modules.empty.action': 'أنشئ الوحدة الأولى',
  'modules.searchPlaceholder': 'ابحث في الوحدات بالاسم أو المعرّف',
  'modules.noMatches': 'لا توجد وحدات مطابقة',
  'modules.noMatches.description': 'لا شيء في الفهرس يطابق «{query}».',
  'modules.configVersion': 'الإعداد إصدار {version}',
  'modules.pluginVersion': 'الإضافة {version}',
  'modules.itemBank': 'بنك الأسئلة',
  'modules.certifiedOf': '{certified} معتمد من {total}',
  'modules.duration': 'المدة',
  'modules.scoringConfig': 'إعداد التقدير',
  'modules.lifecycleActions': 'إجراءات دورة حياة {module}',
  'modules.currently': 'الحالة الآن: {status}',
  'modules.statusHint.active': 'مُتاحة — يمكن إدراجها في خطة شخصية نمطية وتوليدها.',
  'modules.statusHint.draft': 'مؤلَّفة وغير مُتاحة. لا يمكن بدء أي تقييم جديد عليها.',
  'modules.statusHint.retired': 'موقوفة نهائيًا. تبقى النتائج القائمة قابلة للقراءة دائمًا.',
  'modules.action.publish': 'نشر — إتاحتها للمرشّحين',
  'modules.action.unpublish': 'إلغاء النشر — إيقاف التقييمات الجديدة، بشكل قابل للتراجع',
  'modules.action.retire': 'إيقاف نهائي — لن تُقدَّم مرة أخرى أبدًا',
  'modules.action.terminal': 'الإيقاف النهائي لا رجعة فيه — لا شيء يعيدها',
  'modules.published': '«{module}» أصبحت نشِطة',
  'modules.published.detail': 'يمكن ملء كل خلية من خلايا هذه الوحدة.',
  'modules.published.amber.zero': '«{module}» أصبحت نشِطة',
  'modules.published.amber.one': '«{module}» أصبحت نشِطة — لكن خلية واحدة بحالة تحذير',
  'modules.published.amber.two': '«{module}» أصبحت نشِطة — لكن خليتين بحالة تحذير',
  'modules.published.amber.few': '«{module}» أصبحت نشِطة — لكن {count} خلايا بحالة تحذير',
  'modules.published.amber.many': '«{module}» أصبحت نشِطة — لكن {count} خلية بحالة تحذير',
  'modules.published.amber.other': '«{module}» أصبحت نشِطة — لكن {count} خلية بحالة تحذير',
  'modules.published.amber.detail':
    'ستولّد تلك الشخصيات النمطية أداة قصيرة، أو تفشل في التوليد. اعتمد أسئلة إضافية أو أنقص أعداد الخلايا.',
  'modules.publishFailed': 'تعذّر النشر',
  'modules.unpublished': '«{module}» عادت إلى المسودة',
  'modules.unpublished.breaking':
    'لا يمكن بدء أي تقييم جديد عليها. ما زالت {plans} من خطط الشخصيات النمطية تسمّيها وستفشل في التوليد: {names}. أما {open} من التقييمات المفتوحة فلن تتأثر.',
  'modules.unpublished.clean':
    'لا يمكن بدء أي تقييم جديد عليها. أما {open} من التقييمات المفتوحة فلن تتأثر — فقد جمّدت أسئلتها وثبّتت إعدادها.',
  'modules.unpublishFailed': 'تعذّر إلغاء النشر',
  'modules.retired': '«{module}» أُوقفت نهائيًا',
  'modules.retired.detail':
    'لن تُقدَّم مرة أخرى أبدًا. تبقى كل نتيجة ونسخة تقييم وسؤال كما هي تمامًا.',
  'modules.retireFailed': 'تعذّر الإيقاف النهائي',

  /* ------------------------------------------------------ bulk (B5, B6) */
  'table.selectPage': 'تحديد كل الصفوف في هذه الصفحة',
  'table.selectRow': 'تحديد هذا الصف',
  'bulk.selected.zero': 'لم يُحدَّد شيء',
  'bulk.selected.one': 'حُدِّد سؤال واحد',
  'bulk.selected.two': 'حُدِّد سؤالان',
  'bulk.selected.few': 'حُدِّدت {count} أسئلة',
  'bulk.selected.many': 'حُدِّد {count} سؤالًا',
  'bulk.selected.other': 'حُدِّد {count} سؤال',
  'bulk.clear': 'مسح التحديد',
  'bulk.actions': 'إجراءات جماعية',
  'bulk.applying.zero': 'جارٍ التطبيق…',
  'bulk.applying.one': 'جارٍ التطبيق على سؤال واحد…',
  'bulk.applying.two': 'جارٍ التطبيق على سؤالين…',
  'bulk.applying.few': 'جارٍ التطبيق على {count} أسئلة…',
  'bulk.applying.many': 'جارٍ التطبيق على {count} سؤالًا…',
  'bulk.applying.other': 'جارٍ التطبيق على {count} سؤال…',
  'bulk.tags': 'الوسوم',
  'bulk.failed': 'فشلت العملية الجماعية',
  'bulk.applied.zero': 'لم يتغيّر شيء',
  'bulk.applied.one': 'سؤال واحد {verb}',
  'bulk.applied.two': 'سؤالان {verb}',
  'bulk.applied.few': '{count} أسئلة {verb}',
  'bulk.applied.many': '{count} سؤالًا {verb}',
  'bulk.applied.other': '{count} سؤال {verb}',
  'bulk.applied.detail': 'تغيّر كل سؤال في التحديد.',
  'bulk.partial': '{applied} من {requested} {verb}',
  'bulk.newlyAmber.zero': 'لم تتحوّل أي خلية إلى حالة تحذير',
  'bulk.newlyAmber.one': 'خلية واحدة أصبحت بحالة تحذير',
  'bulk.newlyAmber.two': 'خليتان أصبحتا بحالة تحذير',
  'bulk.newlyAmber.few': '{count} خلايا أصبحت بحالة تحذير',
  'bulk.newlyAmber.many': '{count} خلية أصبحت بحالة تحذير',
  'bulk.newlyAmber.other': '{count} خلية أصبحت بحالة تحذير',
  'bulk.verb.certify': 'اعتُمد',
  'bulk.verb.uncertify': 'أُعيد إلى المسودة',
  'bulk.verb.retire': 'أُوقف',
  'bulk.verb.tagged': 'حُدِّث',
  'bulk.tags.title': 'وسم التحديد',
  'bulk.tags.description.zero': 'لا ينطبق على شيء.',
  'bulk.tags.description.one': 'ينطبق على سؤال واحد محدَّد.',
  'bulk.tags.description.two': 'ينطبق على سؤالين محدَّدين.',
  'bulk.tags.description.few': 'ينطبق على {count} أسئلة محدَّدة.',
  'bulk.tags.description.many': 'ينطبق على {count} سؤالًا محدَّدًا.',
  'bulk.tags.description.other': 'ينطبق على {count} سؤال محدَّد.',
  'bulk.addTags': 'إضافة وسوم (مفصولة بفواصل)',
  'bulk.removeTags': 'إزالة وسوم (مفصولة بفواصل)',
  'bulk.audienceTags': 'وسوم الجمهور — تستبدل',
  'bulk.contextTags': 'وسوم السياق — تستبدل',
  'bulk.replaceWarning':
    'وسوم الجمهور والسياق تستبدل ما لدى كل سؤال، لأنها تحدّد خلايا المصفوفة التي يمكن سحب السؤال إليها. أما الوسوم العادية فتُضاف وتُزال.',
  'bulk.applyTags': 'تطبيق على التحديد',

  /* ---------------------------------------------------------------- results */
  'nav.results': 'النتائج',
  'results.description':
    'كل نتيجة مُقدَّرة، الأحدث أولًا. وإعادة التقدير تضيف صفًا بدل أن تستبدل واحدًا.',
  'results.column.flags': 'مؤشرات الجودة',
  'results.firstScoring': 'أول تقدير',
  'results.searchPlaceholder': 'ابحث بمرجع المرشّح…',
  'results.empty.title': 'لا توجد نتائج بعد',
  'results.empty.description':
    'تظهر النتيجة هنا عند تسليم تقييم وتقديره. ابدأ واحدًا من التقديم ← إجراء تقييم.',
  'results.noMatches': 'لا توجد نتائج مطابقة',
  'results.noMatches.description': 'لا شيء يطابق هذه المرشِّحات. امسحها لعرض كل النتائج.',

  /* ------------------------------------------------------------ list state */
  'filters.saved.label': 'العروض',
  'filters.thisScreen': 'المحفوظة لهذه الشاشة',
  'filters.none': 'لم يُحفظ شيء بعد. صفِّ القائمة ثم احفظ العرض.',
  'filters.saveCurrent': 'حفظ هذا العرض',
  'filters.name': 'اسم العرض',
  'filters.namePlaceholder': 'السلوكية المعتمدة',
  'filters.save': 'حفظ',
  'filters.saved': 'حُفظ «{name}»',
  'filters.remove': 'إزالة {name}',
  'questions.empty.clearFilters': 'مسح المرشِّحات',
  'chrome.breadcrumb': 'مسار التنقل',

  /* -------------------------------------------------------------- questions */
  'questions.description':
    'بنك الأسئلة ثنائي اللغة. تُسحَب الأسئلة المعتمدة وحدها إلى التقييم.',
  'questions.column.prompt': 'نص السؤال',
  'questions.column.module': 'الوحدة',
  'questions.column.type': 'النوع',
  'questions.column.status': 'الحالة',
  'questions.filterByType': 'تصفية حسب النوع',
  'questions.filterByStatus': 'تصفية حسب الحالة',
  'questions.allTypes': 'كل الأنواع',
  'questions.allStatuses': 'كل الحالات',
  'questions.searchPlaceholder': 'ابحث في أي من اللغتين…',
  'questions.searchLabel': 'البحث في نصوص الأسئلة',
  'questions.empty.title': 'لا توجد أسئلة مطابقة',
  'questions.empty.description':
    'لا شيء في البنك يطابق هذه المرشِّحات. امسحها أو ألّف سؤالًا جديدًا.',
  'questions.newItem': 'سؤال جديد',
  'questions.exportCsv': 'تصدير CSV',
  'questions.exporting': 'جارٍ التصدير…',
  'questions.importCsv': 'استيراد CSV',
  'questions.export.ready': 'التصدير جاهز',
  'questions.export.hint': 'حرّره في إكسل ثم استورده مجددًا — تحتفظ الصفوف بمعرّف السؤال.',
  'questions.export.failed': 'تعذّر التصدير',

  'questions.action.review': 'مراجعة',
  'questions.action.certify': 'اعتماد',
  'questions.action.uncertify': 'إلغاء الاعتماد',
  'questions.action.retire': 'إيقاف',
  'questions.action.unretire': 'إلغاء الإيقاف',
  'questions.failed.review': 'تعذّر الإرسال للمراجعة',
  'questions.failed.certify': 'تعذّر الاعتماد',
  'questions.failed.uncertify': 'تعذّر إلغاء الاعتماد',
  'questions.failed.retire': 'تعذّر الإيقاف',
  'questions.failed.unretire': 'تعذّر إلغاء الإيقاف',
  'questions.sentForReview': 'أُرسل للمراجعة',
  'questions.sentForReview.detail': '{item} في انتظار مراجع.',
  'questions.certified': 'اعتُمد السؤال',
  'questions.certified.detail': 'أصبح {item} قابلًا للسحب في التقييمات الجديدة.',
  'questions.unretired': 'أُلغي إيقاف السؤال',
  'questions.unretired.detail': 'عاد {item} مسودةً — اعتمده ليصبح قابلًا للسحب.',
  'questions.impact.uncertified': 'أُعيد {item} إلى المسودة',
  'questions.impact.retired': 'أُوقف {item}',
  'questions.impact.uncertified.amber.zero': 'أُعيد {item} إلى المسودة',
  'questions.impact.uncertified.amber.one': 'أُعيد {item} إلى المسودة — خلية واحدة أصبحت بحالة تحذير',
  'questions.impact.uncertified.amber.two': 'أُعيد {item} إلى المسودة — خليتان أصبحتا بحالة تحذير',
  'questions.impact.uncertified.amber.few': 'أُعيد {item} إلى المسودة — {count} خلايا أصبحت بحالة تحذير',
  'questions.impact.uncertified.amber.many': 'أُعيد {item} إلى المسودة — {count} خلية أصبحت بحالة تحذير',
  'questions.impact.uncertified.amber.other': 'أُعيد {item} إلى المسودة — {count} خلية أصبحت بحالة تحذير',
  'questions.impact.retired.amber.zero': 'أُوقف {item}',
  'questions.impact.retired.amber.one': 'أُوقف {item} — خلية واحدة أصبحت بحالة تحذير',
  'questions.impact.retired.amber.two': 'أُوقف {item} — خليتان أصبحتا بحالة تحذير',
  'questions.impact.retired.amber.few': 'أُوقف {item} — {count} خلايا أصبحت بحالة تحذير',
  'questions.impact.retired.amber.many': 'أُوقف {item} — {count} خلية أصبحت بحالة تحذير',
  'questions.impact.retired.amber.other': 'أُوقف {item} — {count} خلية أصبحت بحالة تحذير',
  'questions.impact.amber.detail':
    'لم يعد بإمكان {cells} السحب من البنك المعتمد. سيرفض التوليد تلك الشخصيات النمطية حتى يتعافى المخزون.',
  'questions.impact.pinned.zero': 'ما زالت خضراء، ولم تثبّت أي خلية هذا السؤال صراحةً.',
  'questions.impact.pinned.one': 'ما زالت خضراء، لكن خلية واحدة ثبّتت هذا السؤال صراحةً: {cells}.',
  'questions.impact.pinned.two': 'ما زالت خضراء، لكن خليتين ثبّتتا هذا السؤال صراحةً: {cells}.',
  'questions.impact.pinned.few': 'ما زالت خضراء، لكن {count} خلايا ثبّتت هذا السؤال صراحةً: {cells}.',
  'questions.impact.pinned.many': 'ما زالت خضراء، لكن {count} خلية ثبّتت هذا السؤال صراحةً: {cells}.',
  'questions.impact.pinned.other': 'ما زالت خضراء، لكن {count} خلية ثبّتت هذا السؤال صراحةً: {cells}.',
  'questions.impact.none': 'لم تتغيّر حالة أي خلية في المصفوفة.',

  'questions.editTitle': 'تعديل {item}',
  'questions.editHint':
    'التعديل لا يمكنه بلوغ تقييم جمّد هذا السؤال مسبقًا. ولا تتغيّر حالة دورة حياته بالحفظ.',
  'questions.newHint': 'يُؤلَّف كمسودة. تُسحَب الأسئلة المعتمدة وحدها إلى التقييم.',
  'questions.prompt.en': 'نص السؤال (بالإنجليزية)',
  'questions.prompt.ar': 'نص السؤال (بالعربية)',
  'questions.label.en': 'Label (English)',
  'questions.label.ar': 'التسمية (بالعربية)',
  'questions.dimension': 'البُعد',
  'questions.scaleMin': 'أدنى قيمة للمقياس',
  'questions.scaleMax': 'أعلى قيمة للمقياس',
  'questions.anchors.en': 'مراسي المقياس (بالإنجليزية، مفصولة بـ |)',
  'questions.anchors.ar': 'مراسي المقياس (بالعربية، مفصولة بـ |)',
  'questions.anchors.placeholder': 'Strongly disagree | … | Strongly agree',
  'questions.reverseKeyed': 'معكوس المفتاح — يُقلَب المقياس قبل التقدير',
  'questions.tags': 'الوسوم (مفصولة بفواصل)',
  'questions.skillId': 'معرّف المهارة (اختياري)',
  'questions.createDraft': 'إنشاء سؤال كمسودة',
  'questions.saved': 'حُفظ السؤال',
  'questions.saved.detail': 'حُدِّث {item}. لا تتأثر نسخ التقييم التي جمّدته مسبقًا.',
  'questions.created': 'أُنشئ السؤال',
  'questions.created.detail': '{item} — مسودة وغير قابل للسحب بعد. اعتمده لإطلاقه.',
  'questions.saveFailed': 'تعذّر حفظ السؤال',

  'questions.options': 'الخيارات',
  'questions.optionsOf': '{count} من {max} (G1)',
  'questions.option.add': 'إضافة خيار',
  'questions.option.moveUp': 'تحريك الخيار {n} لأعلى',
  'questions.option.moveDown': 'تحريك الخيار {n} لأسفل',
  'questions.option.remove': 'إزالة الخيار {n}',
  'questions.option.labelEn': 'تسمية الخيار {n} (بالإنجليزية)',
  'questions.option.labelAr': 'تسمية الخيار {n} (بالعربية)',
  'questions.option.isCorrect': 'الخيار {n} هو الصحيح',
  'questions.option.correctAnswer': 'الإجابة الصحيحة',
  'questions.option.value': 'قيمة الفاعلية',
  'questions.option.valueLabel': 'قيمة فاعلية الخيار {n}',
  'questions.option.anchor': 'مستوى المرسى (1 = الأدنى)',
  'questions.option.anchorLabel': 'مستوى مرسى الخيار {n}',
  'questions.option.dimension': 'البُعد الذي يقيسه هذا الخيار',
  'questions.option.dimensionLabel': 'بُعد الخيار {n}',

  'questions.problem.promptEn': 'نص السؤال بالإنجليزية مطلوب.',
  'questions.problem.noFieldSet': 'لا توجد مجموعة حقول لنوع السؤال «{type}».',
  'questions.problem.dimension': 'يجب أن يسمّي سؤال المقياس البُعد الذي يقيسه.',
  'questions.problem.scaleRange': 'يجب أن تكون أعلى قيمة للمقياس أكبر من أدناها.',
  'questions.problem.anchorsBoth':
    'تحتاج المراسي إلى تسميتين على الأقل في كلتا اللغتين، أو لا شيء إطلاقًا.',
  'questions.problem.minOptions': 'يحتاج {type} إلى {min} خيارات على الأقل.',
  'questions.problem.maxOptions': 'يسمح {type} بـ {max} خيارات كحد أقصى.',
  'questions.problem.optionLabel': 'الخيار {n} بلا تسمية بالإنجليزية.',
  'questions.problem.optionValue': 'يحتاج الخيار {n} إلى قيمة فاعلية.',
  'questions.problem.optionAnchor': 'يحتاج الخيار {n} إلى مستوى مرسى.',
  'questions.problem.optionDimension': 'يحتاج الخيار {n} إلى بُعد.',
  'questions.problem.oneCorrect': 'يجب تحديد خيار واحد بالضبط على أنه الصحيح.',

  /* ------------------------------------------------- where an item fits (B6) */
  'questions.cells.title': 'يُسحَب إلى',
  'questions.cells.detail':
    'خلايا المصفوفة التي يطابق هذا السؤال استعلام مخزونها، بحسب وحدته ووسومه وجمهوره وسياقه. غيّر وسمًا أعلاه ويتغيّر هذا معه.',
  'questions.cells.none':
    'لا توجد خلية تسحب هذا السؤال. فوحدته أو وسومه أو جمهوره لا تطابق أي استعلام مخزون.',
  'questions.cells.pinned': 'مثبَّت',
  'questions.cells.notCertified':
    'لن يُسحَب إلا بعد اعتماده — وهذه هي الخلايا التي سيدخلها.',

  /* ------------------------------------------------------------- item types */
  'itemType.likert': 'ليكرت',
  'itemType.likert.hint': 'مقياس موافقة على بُعد واحد. تحدّد المراسي معنى كل نقطة.',
  'itemType.slider': 'شريط تمرير',
  'itemType.slider.hint': 'مقياس متصل على بُعد واحد، يُطبَّع مقابل مقاييس الوحدة الأخرى.',
  'itemType.forced_choice': 'اختيار مُلزِم',
  'itemType.forced_choice.hint':
    'مقارن ذاتيًا: كل خيار يقيس بُعدًا مختلفًا، فاختيار أحدها رفض للبقية.',
  'itemType.ranking': 'ترتيب',
  'itemType.ranking.hint': 'الإجابة ترتيب لا اختيار. كل خيار يقيس بُعدًا بحسب موقعه.',
  'itemType.mcq': 'اختيار من متعدد',
  'itemType.mcq.hint': 'خيار واحد بالضبط هو الصحيح.',
  'itemType.sjt': 'حكم موقفي',
  'itemType.sjt.hint': 'كل خيار يحمل قيمة فاعلية — لا توجد إجابة صحيحة واحدة.',
  'itemType.bars': 'مقاييس مرتكزة سلوكيًا',
  'itemType.bars.hint': 'مرتكزة سلوكيًا: كل خيار يصف مستوى من الأداء.',

  /* ---------------------------------------------------------------- scoring */
  'scoring.description':
    'الأبعاد ونطاقات التفسير ومعايير الشخصيات النمطية وقواعد ضبط الجودة — بيانات لا شيفرة.',
  'scoring.registered': '{count} مُسجَّلة',
  'scoring.unreachable': 'تعذّر الوصول إلى واجهة برمجة التطبيقات',
  'scoring.unreachable.description':
    'سجلّ التقدير يُبلَّغ عنه عبر GET /api/health، ولم يستجب.',
  'scoring.empty.title': 'لا توجد إضافات تقدير مُسجَّلة',
  'scoring.empty.description':
    'الواجهة البرمجية متاحة لكن سجلّها فارغ، لذا لا يمكن لهذه النسخة تقدير أي تقييم مُسلَّم. تُسجَّل الإضافات عند الإقلاع.',
  'scoring.registeredAtBoot': 'مُسجَّلة عند الإقلاع',
  'scoring.model.unknown': 'نموذج تقدير مُسجَّل.',
  'scoring.model.RIASEC': 'ميول هولاند المهنية — ستة أنماط تُعرض كرمز هولاند. رسم شبكي.',
  'scoring.model.BIG_FIVE': 'سمات العوامل الخمسة الكبرى مع نطاقات تفسير ومؤشرات جودة. أعمدة.',
  'scoring.model.IRT_BARS': 'أسئلة معرفية وفق نظرية الاستجابة للمفردة مع مراسٍ ذاتية BARS لكل مهارة. مقياس.',
  'scoring.provenance':
    'مقروء مباشرةً من GET /api/health. تُصدَّر إعدادات التقدير لكل وحدة وتُثبَّت على نسخة التقييم عند التوليد، لذا فتعديل إعداد لا يمكنه تغيير نتيجة قائمة؛ وإعادة التقدير تكتب مغلّفًا جديدًا غير قابل للتغيير ولا تمسّ التاريخ. عدّل الإعداد من وحدات التقييم، حيث يظهر الإصدار الذي سيُستحدث وعدد التقييمات المثبَّتة على الإصدار الحالي قبل الحفظ.',

  /* --------------------------------------------------------- question types */
  'questionTypes.description':
    'سجلّ أنواع الأسئلة — أي الوحدات تستخدم كل نوع، وكيف يُقدَّر.',
  'questionTypes.count.zero': 'لا توجد أنواع',
  'questionTypes.count.one': 'نوع واحد',
  'questionTypes.count.two': 'نوعان',
  'questionTypes.count.few': '{count} أنواع',
  'questionTypes.count.many': '{count} نوعًا',
  'questionTypes.count.other': '{count} نوع',
  'questionTypes.loadFailed': 'تعذّر تحميل السجلّ',
  'questionTypes.certifiedCount': '{count} معتمد',
  'questionTypes.usedBy': 'تستخدمه',
  'questionTypes.unused': 'لا توجد وحدة تُعلن هذا النوع.',
  'questionTypes.breakdown': '{total} في البنك · {draft} مسودة · {retired} موقوف',
  'questionTypes.undescribed': 'نوع موجود في البنك وغير موصوف هنا.',
  'questionTypes.scoredByPlugin': 'يُقدَّر بالإضافة التي ترتبط بها وحدته.',
  'questionTypes.openRegistry':
    'الأنواع سجلّ يعمل وقت التشغيل لا تعدادًا في الشيفرة — يتحقّق العقد من itemType كنص مطابق للصيغة مقابل المجموعة الحية، وهو ما يتيح ظهور نوع جديد دون نشر. والنوع الموجود في البنك وغير الموصوف أعلاه يظهر هنا أيضًا، وهذه بالضبط الحالة التي وُجد السجلّ المفتوح من أجلها.',
  'questionTypes.noToggle':
    'لا يوجد مفتاح تفعيل/تعطيل: تقييد الأنواع المسموح بتأليفها يحتاج مجموعة مخزّنة من الأنواع المفعّلة، ولا وجود لها بعد. ومفتاح يبدو ذا سلطة ولا يفعل شيئًا أسوأ من غيابه.',
  'questionTypes.answer.likert': 'نقطة واحدة على مقياس موافقة.',
  'questionTypes.scoring.likert':
    'يُضاف إلى بُعده، من الصفر، ويُقلَب أولًا إن كان معكوس المفتاح.',
  'questionTypes.answer.slider': 'موضع متصل على مقياس.',
  'questionTypes.scoring.slider':
    'يُطبَّع مقابل مداه، فيتساوى وزن شريط 0–100 مع ليكرت 1–5.',
  'questionTypes.answer.forced_choice': 'اختر واحدًا من اثنين أو ثلاثة.',
  'questionTypes.scoring.forced_choice':
    'مقارن ذاتيًا — يكسب بُعد الخيار المختار، والبقية كانت متاحة ولم تُختر.',
  'questionTypes.answer.ranking': 'رتّب كل الخيارات.',
  'questionTypes.scoring.ranking': 'موزون بالموضع: المركز الأول يساوي N والأخير يساوي 1.',
  'questionTypes.answer.mcq': 'اختر خيارًا واحدًا؛ واحد بالضبط هو الصحيح.',
  'questionTypes.scoring.mcq': 'صحيح أو لا، موزونًا بمعامل تمييز السؤال.',
  'questionTypes.answer.sjt': 'اختر أفضل استجابة لموقف.',
  'questionTypes.scoring.sjt':
    'قيمة فاعلية الخيار المختار، منسوبةً إلى أفضل قيمة متاحة في ذلك السؤال.',
  'questionTypes.answer.bars': 'اختر المرسى السلوكي المناسب.',
  'questionTypes.scoring.bars': 'مستوى المرسى، موضوعًا على المقياس بالصيغة (المستوى−1)/(k−1).',

  /* ------------------------------------------------------------- candidates */
  'candidates.description':
    'نسخ التقييم بحسب مرجع المرشّح — معرّفات مبهمة فقط، بلا بيانات شخصية.',
  'candidates.count.zero': 'لا يوجد مرشّحون',
  'candidates.count.one': 'مرشّح واحد',
  'candidates.count.two': 'مرشّحان',
  'candidates.count.few': '{count} مرشّحين',
  'candidates.count.many': '{count} مرشّحًا',
  'candidates.count.other': '{count} مرشّح',
  'candidates.inFlight': '{count} قيد التنفيذ',
  'candidates.feedSilent': 'لم تستجب تغذية نسخ التقييم.',
  'candidates.empty.title': 'لا يوجد مرشّحون بعد',
  'candidates.empty.description':
    'يُتابَع المرشّحون بمرجع مبهم حتى لا تحتفظ المنصة بأي بيانات تعريف شخصية. وتظهر نسخ التقييم هنا بمجرد تشغيل المسار.',
  'candidates.searchPlaceholder': 'ابحث بمرجع المرشّح أو الوحدة أو الشخصية النمطية…',
  'candidates.noMatches': 'لا توجد نتائج مطابقة',
  'candidates.noMatches.description': 'لا شيء يطابق «{query}».',
  'candidates.instances.zero': 'لا توجد نسخ تقييم',
  'candidates.instances.one': 'نسخة تقييم واحدة',
  'candidates.instances.two': 'نسختا تقييم',
  'candidates.instances.few': '{count} نسخ تقييم',
  'candidates.instances.many': '{count} نسخة تقييم',
  'candidates.instances.other': '{count} نسخة تقييم',
  'candidates.results.zero': 'لا توجد نتائج',
  'candidates.results.one': 'نتيجة واحدة',
  'candidates.results.two': 'نتيجتان',
  'candidates.results.few': '{count} نتائج',
  'candidates.results.many': '{count} نتيجة',
  'candidates.results.other': '{count} نتيجة',
  'candidates.answered': 'تمت الإجابة عن {answered}/{total} (%{progress})',
  'candidates.viewResult': 'عرض النتيجة',
  'candidate.title': 'المرشّح',
  'candidate.journey': 'خطة التقييم',
  'candidate.journey.hint':
    'كل وحدة رُبط بها هذا المرشّح، بترتيب الخطة — بما فيها ما لم يبدأه بعد.',
  'candidate.completeOf': 'اكتمل {completed} من {total}',
  'candidate.notStarted': 'لم يبدأ',
  'candidate.resume': 'استئناف التشغيل',
  'candidate.notFound': 'لا يوجد مرشّح بهذا المعرّف',
  'candidate.backToList': 'العودة إلى المرشّحين',
  'candidate.noPlan': 'لا توجد خطة لعرضها',
  'candidate.noPlan.hint':
    'لهذا المرشّح تشغيلات لكن شخصيته النمطية لا تسمّي أي وحدة. انشر خطة للشخصية النمطية لتظهر الرحلة هنا.',
  'candidate.unregistered':
    'حُلَّت البيانات من تشغيلات هذا المرشّح — لم يُسجَّل عبر واجهة التكامل، وهذا طبيعي لتشغيل تجريبي بدأ من هذه الواجهة.',
  'candidate.planUnresolvable':
    'تعذّر العثور على نسخة الخطة {version} في سجل الشخصية النمطية، لذا فالخطة أدناه هي خطة اليوم لا التي رُبط بها هذا المرشّح.',
  'candidate.lineage': 'سجل النتائج',
  'candidate.lineage.hint':
    'النتائج تُضاف ولا تُستبدل — إعادة التقدير تكتب نتيجة جديدة وتحتفظ بالقديمة. هذه كل نتائج هذا المرشّح، الأحدث أولًا.',
  'candidate.registeredAt': 'سُجِّل {when}',
  'candidates.provenance':
    'مقروء مباشرةً من GET /api/assessments و GET /api/results. يظهر التقييم لحظة توليده — ولم تكن تغذية النتائج وحدها قادرة على إظهار تقييم قيد التنفيذ.',

  /* ----------------------------------------------------------- persona editor */
  'personaEditor.loading': 'جارٍ تحميل الشخصية النمطية…',
  'personaEditor.description':
    'تعديل الخطة مسودة حتى تنشرها. والنشر يُصدر نسخة جديدة ويُبقي القديمة قابلة للاستدعاء، فيظل التقييم المولَّد سلفًا يعرف ما قُدِّم به.',
  'personaEditor.unpublishedDraft': 'مسودة غير منشورة',
  'personaEditor.segment': 'الشريحة',
  'personaEditor.defaultLocale': 'لغة التقديم الافتراضية',
  'personaEditor.saveDetails': 'حفظ البيانات',
  'personaEditor.detailsSaved': 'حُفظت البيانات',
  'personaEditor.detailsSaved.detail': 'الأسماء والشريحة تسميات — لا شيء مثبَّت عليها.',
  'personaEditor.saveFailed': 'تعذّر الحفظ',
  'personaEditor.planTemplate': 'قالب الخطة',
  'personaEditor.editingDraft': 'تعديل المسودة غير المنشورة',
  'personaEditor.editingCopy': 'تعديل نسخة من {version}',
  'personaEditor.addModule': 'إضافة وحدة',
  'personaEditor.emptyPlan':
    'هذه الخطة لا تسمّي أي وحدة. ولو نُشرت كما هي لرُفض كل مرشّح لهذه الشخصية النمطية — أوقفها بدلًا من ذلك إن كان هذا ما تقصده.',
  'personaEditor.moduleAt': 'الوحدة في الموضع {n}',
  'personaEditor.itemCountFor': 'عدد أسئلة {module}',
  'personaEditor.required': 'مطلوبة',
  'personaEditor.requiredFor': '{module} مطلوبة',
  'personaEditor.moveUp': 'تحريك {module} لأعلى',
  'personaEditor.moveDown': 'تحريك {module} لأسفل',
  'personaEditor.remove': 'إزالة {module}',
  'personaEditor.itemCountNote':
    'الأعداد هنا افتراضات هذه الخطة. وحيث توجد خلية مصفوفة فهي التي تقرّر ما يُطرح فعلًا، والمعاينة تبيّن ذلك حين يختلفان.',
  'personaEditor.saveDraft': 'حفظ المسودة',
  'personaEditor.draftSaved': 'حُفظت المسودة',
  'personaEditor.draftSaved.detail':
    'لا شيء يُستدعى من مسودة. يواصل المرشّحون أداء الخطة المنشورة حتى تنشر هذه.',
  'personaEditor.draftSaveFailed': 'تعذّر حفظ المسودة',
  'personaEditor.discardDraft': 'تجاهل المسودة',
  'personaEditor.discarded': 'أُلغيت المسودة',
  'personaEditor.discarded.detail': 'عودة إلى {version}، التي لم تتوقّف يومًا عن السريان.',
  'personaEditor.discardFailed': 'تعذّر الإلغاء',
  'personaEditor.publish': 'النشر',
  'personaEditor.unsaved':
    'توجد تغييرات غير محفوظة أعلاه. احفظ المسودة قبل المعاينة، وإلا وصفت المعاينة الخطة التي بدأت منها.',
  'personaEditor.previewAction': 'معاينة التغييرات والتغطية',
  'personaEditor.previewFailed': 'تعذّرت المعاينة',
  'personaEditor.willMint': 'سيُصدر النشر {next} ويحلّ محل {current}، التي تبقى قابلة للاستدعاء.',
  'personaEditor.nothingToPublish': 'لا شيء لنشره — هذه تغطية {current} كما هي.',
  'personaEditor.pinnedRuns':
    '{open} تقييمًا مفتوحًا و{completed} تقييمًا مكتملًا مثبَّتة على {version}. ولن تتأثر — فالنسخة التي ثبّتتها تبقى قابلة للاستدعاء.',
  'personaEditor.whatChanges': 'ما الذي يتغيّر',
  'personaEditor.change.added': 'مُضافة',
  'personaEditor.change.changed': 'مُعدَّلة',
  'personaEditor.change.removed': 'مُزالة',
  'personaEditor.change.unchanged': 'دون تغيير',
  'personaEditor.coverage': 'التغطية، مقابل هذه الخطة',
  'personaEditor.asksFor': 'تطلب {count}',
  'personaEditor.superseded': 'النسخ السابقة',
  'personaEditor.superseded.hint':
    'محفوظة كاملة لا مذكورة فحسب. فالتقييم المثبَّت على إحداها يمكن أن يُخبَر بدقة بما قُدِّم به.',
  'personaEditor.noModules': 'لا وحدات',
  'personaEditor.publishPlan': 'نشر الخطة',
  'personaEditor.publishing': 'جارٍ النشر…',
  'personaEditor.published': 'الخطة {version} سارية',
  'personaEditor.published.detail':
    'تبقى النسخة السابقة قابلة للاستدعاء لكل تقييم مثبَّت عليها.',
  'personaEditor.publishFailed': 'تعذّر النشر',
  'personaEditor.create.description':
    'فئة جمهور، وخطة الوحدات التي تؤديها. تأتي الخطة لاحقًا — إذ تُنشأ الشخصية النمطية بلا خطة، لأن الخطة لا معنى لها إلا مقابل خلايا المصفوفة والبنك المعتمد خلفها.',
  'personaEditor.personaId': 'معرّف الشخصية النمطية',
  'personaEditor.personaId.invalid':
    'أحرف صغيرة بنقطة وشرطة سفلية، من 4 إلى 64 حرفًا — مثل persona.high_schooler.',
  'personaEditor.createPersona': 'إنشاء شخصية نمطية',
  'personaEditor.created': 'أُنشئت «{persona}»',
  'personaEditor.created.detail':
    'لا تقدّم شيئًا بعد. أضِف وحدات إلى خطتها، وتحقّق من التغطية، ثم انشر.',
  'personaEditor.createFailed': 'تعذّر الإنشاء',

  /* ------------------------------------------------- module create wizard */
  'createModule.title': 'وحدة تقييم جديدة',
  'createModule.description':
    'تربط الوحدة إضافة تقدير مُسجَّلة ببنك أسئلة. أما إضافة نموذج نفسي جديد فهي نشر شيفرة لا استمارة — ولا تظهر أدناه سوى النماذج المنشورة فعلًا.',
  'createModule.step1': 'الهوية',
  'createModule.step1.hint': 'ثنائية اللغة، كحال كل حقل محتوى في هذه المنصة.',
  'createModule.moduleId': 'معرّف الوحدة',
  'createModule.moduleId.detail':
    'أحرف إنجليزية صغيرة وشرطات سفلية، من 3 إلى 64 حرفًا. دائم بعد الإنشاء، لأن كل نتيجة تشير إليه.',
  'createModule.moduleId.invalid': 'يجب أن يطابق lower_snake_case، من 3 إلى 64 حرفًا.',
  'createModule.minutes': 'المدة التقديرية بالدقائق',
  'createModule.name.en': 'الاسم (بالإنجليزية)',
  'createModule.name.ar': 'الاسم (بالعربية)',
  'createModule.description.en': 'الوصف (بالإنجليزية)',
  'createModule.description.ar': 'الوصف (بالعربية)',
  'createModule.step2': 'نموذج التقدير',
  'createModule.step2.hint':
    'مقروء من سجل الإضافات الحي. هذا الاختيار يحدّد الشيفرة التي تقدّر كل نتيجة.',
  'createModule.scores': 'يقدّر {types}',
  'createModule.buildsRetained': '{count} إصدارًا محفوظًا',
  'createModule.noPlugins':
    'لا توجد إضافة تقدير مُسجَّلة، فلا يمكن إنشاء أي وحدة. تُسجَّل الإضافات عند الإقلاع — وهذه النسخة لا تستطيع تقدير أي شيء.',
  'createModule.step3': 'أنواع الأسئلة المدعومة',
  'createModule.step3.hint':
    'مقيَّدة بالنموذج المختار — فالسؤال الذي لا تستطيع إضافته تقديره يُرفض عند التأليف.',
  'createModule.needAType': 'نوع واحد على الأقل — فالوحدة التي لا تدعم شيئًا لا يمكن أن تحمل سؤالًا.',
  'createModule.step4': 'إعداد التقدير',
  'createModule.step4.hint': 'مُهيَّأ من شكل النموذج نفسه، وتتحقّق منه إضافته عند الحفظ.',
  'createModule.noTemplate':
    'لا ينشر {model} إعدادًا مبدئيًا، لذا يبدأ هذا فارغًا. وستظل إضافته تتحقّق مما تُدخله.',
  'createModule.version1': 'الإصدار 1',
  'createModule.versionShort': 'يُضبط تلقائيًا',
  'createModule.versionNote':
    'يُضبَط على الخادم ولا يمكن تعديله هنا — فالإصدار هو ما تثبّته نسخة التقييم.',
  'createModule.nothingCreated': 'لم يُنشأ شيء:',
  'createModule.createDraft': 'إنشاء كمسودة',
  'createModule.created': 'أُنشئت الوحدة «{module}» كمسودة',
  'createModule.created.detail':
    'ألّف بنكها واعتمده، ثم انشرها — والنشر يُبلِّغ بالتغطية التي تهبط فيها.',
  'createModule.badJson': 'إعداد التقدير ليس JSON صالحًا: {error}',
  'createModule.failed': 'فشل الإنشاء',

  /* ------------------------------------------------------ scoring config editor */
  'config.title': 'إعداد التقدير — {module}',
  'config.description':
    'بيانات لا شيفرة. تتحقّق منها إضافة التقدير الخاصة بهذه الوحدة، ولا يتغيّر الإصدار إلا حين يمكن للتغيير أن يبدّل درجة.',
  'config.versionHistory': 'سجل الإصدارات',
  'config.jsonLabel': 'إعداد التقدير بصيغة JSON',
  'config.absent': '(غير موجود)',
  'config.changesScores': 'يغيّر الدرجات',
  'config.displayOnly': 'عرض فقط',
  'config.noHistory': 'لا توجد إصدارات سابقة. لم تُقدِّر هذه الوحدة إلا بالإصدار {version}.',
  'config.history.hint':
    'للقراءة فقط، وعن قصد. الإعداد السابق هو ما يستدعيه تقييم جارٍ عند التسليم — وتعديله يعيد كتابة ما يُقدَّم به تقييم بدأ فعلًا. لتغيير طريقة التقدير عدّل الإصدار الحالي؛ ولتطبيقه على تقييم انتهى أعِد التقدير.',
  'config.dimensionsBands': '{dimensions} بُعدًا · {bands} نطاقًا',
  'config.notJson': 'ليس JSON صالحًا',
  'config.notJsonDetail': 'ليس JSON صالحًا: {error}',
  'config.requestFailed': 'فشل الطلب',
  'config.rejected': 'رُفض هذا الإعداد ولم يُحفظ شيء:',
  'config.identical': 'هذا مطابق للإعداد المحفوظ — لن يتغيّر شيء.',
  'config.versions': 'هذا يُصدر الإعداد: من v{from} إلى v{to}.',
  'config.pinnedOpen.zero': 'لا يوجد تقييم مفتوح مثبَّت على v{version}.',
  'config.pinnedOpen.one':
    'تقييم واحد جارٍ مثبَّت على v{version} وسيظل يُقدَّر به — إذ يُحفظ v{version} في سجل الإصدارات ولا يُستبدل.',
  'config.pinnedOpen.two':
    'تقييمان جاريان مثبَّتان على v{version} وسيظلان يُقدَّران به — إذ يُحفظ v{version} في سجل الإصدارات ولا يُستبدل.',
  'config.pinnedOpen.few':
    '{count} تقييمات جارية مثبَّتة على v{version} وستظل تُقدَّر به — إذ يُحفظ v{version} في سجل الإصدارات ولا يُستبدل.',
  'config.pinnedOpen.many':
    '{count} تقييمًا جاريًا مثبَّتًا على v{version} وسيظل يُقدَّر به — إذ يُحفظ v{version} في سجل الإصدارات ولا يُستبدل.',
  'config.pinnedOpen.other':
    '{count} تقييم جارٍ مثبَّت على v{version} وسيظل يُقدَّر به — إذ يُحفظ v{version} في سجل الإصدارات ولا يُستبدل.',
  'config.pinnedCompleted.zero': 'ولا توجد نتائج منتهية سجّلته.',
  'config.pinnedCompleted.one':
    'وسجّلته نتيجة منتهية واحدة لا يُعاد حسابها أبدًا؛ وإعادة التقدير هي السبيل الوحيد لبلوغها بإصدار جديد.',
  'config.pinnedCompleted.two':
    'وسجّلته نتيجتان منتهيتان لا يُعاد حسابهما أبدًا؛ وإعادة التقدير هي السبيل الوحيد لبلوغهما بإصدار جديد.',
  'config.pinnedCompleted.few':
    'وسجّلته {count} نتائج منتهية لا يُعاد حسابها أبدًا؛ وإعادة التقدير هي السبيل الوحيد لبلوغها بإصدار جديد.',
  'config.pinnedCompleted.many':
    'وسجّلته {count} نتيجة منتهية لا يُعاد حسابها أبدًا؛ وإعادة التقدير هي السبيل الوحيد لبلوغها بإصدار جديد.',
  'config.pinnedCompleted.other':
    'وسجّلته {count} نتيجة منتهية لا يُعاد حسابها أبدًا؛ وإعادة التقدير هي السبيل الوحيد لبلوغها بإصدار جديد.',
  'config.displayOnlyChange':
    'تغيير عرض فقط — يبقى الإصدار عند v{version}، فلا يتأثر أي تقييم جارٍ.',
  'config.checking': 'جارٍ الفحص…',
  'config.preview': 'معاينة التغييرات',
  'config.previewFirst': 'عايِن التغيير أولًا — فهو يبيّن ما الذي سيُصدره الحفظ',
  'config.save': 'حفظ',
  'config.saved': 'حُفظ إعداد التقدير',
  'config.saved.detail':
    'الإصدار {version} — تحتفظ نسخ التقييم المولَّدة سلفًا بالإصدار الذي ثبّتته.',

  /* ------------------------------------------------------------- CSV import */
  'import.title': 'استيراد أسئلة من CSV',
  'import.description':
    'لا يُكتب شيء حتى تؤكّد. الصفوف التي تحمل itemId تحدّث ذلك السؤال؛ والصفوف بلا معرّف تُدرَج جديدة.',
  'import.csvFile': 'ملف CSV',
  'import.checking': 'جارٍ فحص الملف…',
  'import.unrecognised': '{count} عمودًا غير معروف',
  'import.unrecognised.hint':
    'العمود غير المربوط يُتجاهَل. وإن كان أحدها نص سؤال أو مفتاح تقدير، فربطه هو الفرق بين استيراد واستيراد يُفرِّغ حقلًا.',
  'import.mapColumn': 'ربط العمود {header}',
  'import.ignoreColumn': 'تجاهل هذا العمود',
  'import.problems': '{count} مشكلة',
  'import.allOrNothing':
    'لن يُكتب شيء حتى تصحّ كل الصفوف — فالاستيراد المطبَّق جزئيًا لا يمكن إعادة تشغيله دون تكرار.',
  'import.rowError': 'الصف {row}{path} — {message}',
  'import.andMore': '…و{more} غيرها.',
  'import.column.row': 'الصف',
  'import.column.item': 'السؤال',
  'import.newRow': '— جديد —',
  'import.truncated': 'يُعرض أول 100 من {total} صفًا. وكلها مفحوصة.',
  'import.willIgnore': 'سيُتجاهَل {count} عمودًا.',
  'import.applied': 'طُبِّق الاستيراد',
  'import.applied.detail': 'أُنشئ {created}، وحُدِّث {updated}. ولم يُمسّ شيء آخر في البنك.',
  'import.action.create': 'إنشاء',
  'import.action.update': 'تحديث',
  'import.action.skip': 'تخطٍّ',
  'import.action.error': 'خطأ',

  /* ----------------------------------------------------------- console users */
  'users.column.user': 'المستخدم',
  'users.column.lastSignIn': 'آخر تسجيل دخول',
  'users.never': 'لم يحدث',
  'users.status.active': 'نشِط',
  'users.status.deactivated': 'مُعطَّل',
  'users.changeRole': 'تغيير الدور',
  'users.deactivate': 'تعطيل',
  'users.reactivate': 'إعادة التفعيل',
  'users.newUser': 'مستخدم جديد',
  'users.enforcementNote':
    'تُطبَّق الأدوار على الخادم في كل طلب — وإخفاء عنصر في الواجهة ليس حدًّا أمنيًا. وترفض المنصة تعطيل آخر مسؤول أو خفض دوره.',
  'users.empty.title': 'لا يوجد مستخدمو واجهة',
  'users.empty.description': 'يُنشأ أول مسؤول بأمر create-admin في سطر الأوامر.',
  'users.updated': 'حُدِّث المستخدم',
  'users.updated.detail': '{email} — {role}، {status}',
  'users.updateFailed': 'تعذّر التحديث',
  'users.new.title': 'مستخدم واجهة جديد',
  'users.new.description':
    'تُولَّد كلمة مرور وتُعرض مرة واحدة. يستطيع المحللون قراءة كل شيء دون تغيير أي شيء.',
  'users.role.analyst': 'محلل — قراءة فقط',
  'users.role.admin': 'مسؤول — يستطيع التأليف والتشغيل والنشر',
  'users.role.psychometrician': 'أخصائي قياس نفسي — يؤلّف ويشغّل، ولا ينشر',
  'users.create': 'إنشاء مستخدم',
  'users.creating': 'جارٍ الإنشاء…',
  'users.createFailed': 'تعذّر إنشاء المستخدم',
  'users.created.title': 'أُنشئ الحساب',
  'users.created.description':
    'تُعرض كلمة المرور هذه مرة واحدة ولا يمكن استرجاعها — إذ لا تُحفظ سوى بصمتها. سلّمها عبر قناة خارجية؛ وعلى المستخدم تغييرها عند أول تسجيل دخول.',

  /* --------------------------------------------------- catalogue portability */
  'portability.export.title': 'تصدير هذا الفهرس',
  'portability.export.description':
    'الوحدات والشخصيات النمطية وخلايا المصفوفة في حزمة واحدة. ولا تُدرَج النتائج ونسخ التقييم أبدًا — فهي سجلات لما حدث في هذه البيئة، ونسخها إلى مكان آخر تلفيق للتاريخ.',
  'portability.includeItems': 'تضمين بنك الأسئلة',
  'portability.exportAction': 'تصدير الفهرس',
  'portability.exported': 'صُدِّر الفهرس',
  'portability.exported.detail': '{modules} وحدة، {personas} شخصية نمطية، {cells} خلية.',
  'portability.exported.items': 'بالإضافة إلى {items} سؤالًا.',
  'portability.exported.excluded': 'بلا نتائج أو نسخ تقييم — فتلك سجلات لما حدث هنا.',
  'portability.import.title': 'استيراد فهرس',
  'portability.import.description':
    'التشغيل التجريبي أولًا، دائمًا. تنتقل الإصدارات كما تصل — تحتفظ الوحدة بـscoringConfig.version وتحتفظ الشخصية النمطية بـplanVersion — وتُرفض الحزمة التي تحمل إصدارًا أقدم من إصدار هذه البيئة بدل تطبيقها.',
  'portability.bundleFile': 'ملف الحزمة',
  'portability.checking': 'جارٍ فحص الحزمة…',
  'portability.notJson': 'هذا الملف ليس JSON. حزمة الفهرس هي الملف الذي تصدّره هذه الشاشة.',
  'portability.toCreate': '{count} للإنشاء',
  'portability.toUpdate': '{count} للتحديث',
  'portability.unchanged': '{count} دون تغيير',
  'portability.conflicts': '{count} تعارضًا',
  'portability.from': 'من {source}',
  'portability.blocked': 'لن يُكتب شيء ما دام أي كيان متعارضًا.',
  'portability.column.kind': 'النوع',
  'portability.column.id': 'المعرّف',
  'portability.column.action': 'الإجراء',
  'portability.apply': 'تطبيق الاستيراد',
  'portability.applied': 'تم الاستيراد',
  'portability.imported': 'استُورد الفهرس',
  'portability.importFailed': 'فشل الاستيراد',
  'portability.kind.modules': 'وحدة',
  'portability.kind.personas': 'شخصية نمطية',
  'portability.kind.matrixCells': 'خلية مصفوفة',
  'portability.kind.questions': 'سؤال',
  'portability.action.create': 'إنشاء',
  'portability.action.update': 'تحديث',
  'portability.action.skip': 'تخطٍّ',
  'portability.action.conflict': 'تعارض',

  /* ------------------------------------------------------- module readiness */
  'readiness.availableNeeded': '{available} متاح / {needed} مطلوب',
  'readiness.noItems':
    'لا توجد أسئلة في هذه الوحدة بعد، فلا شيء يمكن أن يُعطَّل — كما أنها لا تستطيع ملء خلية حتى يُؤلَّف بنكها ويُعتمَد.',
  'readiness.allFit.zero': 'لا توجد أسئلة لفحصها، ولا تتغيّر حالة أي خلية.',
  'readiness.allFit.one': 'السؤال الوحيد ما زال ملائمًا لهذه الوحدة، ولا تتغيّر حالة أي خلية.',
  'readiness.allFit.two': 'السؤالان ما زالا ملائمين لهذه الوحدة، ولا تتغيّر حالة أي خلية.',
  'readiness.allFit.few': 'كل الأسئلة الـ{count} ما زالت ملائمة لهذه الوحدة، ولا تتغيّر حالة أي خلية.',
  'readiness.allFit.many': 'كل الأسئلة الـ{count} ما زالت ملائمة لهذه الوحدة، ولا تتغيّر حالة أي خلية.',
  'readiness.allFit.other': 'كل الأسئلة الـ{count} ما زالت ملائمة لهذه الوحدة، ولا تتغيّر حالة أي خلية.',
  'readiness.alreadyAmber.zero': 'لم تكن أي خلية بحالة تحذير قبل هذا التغيير.',
  'readiness.alreadyAmber.one': 'كانت خلية واحدة بحالة تحذير قبل هذا التغيير.',
  'readiness.alreadyAmber.two': 'كانت خليتان بحالة تحذير قبل هذا التغيير.',
  'readiness.alreadyAmber.few': 'كانت {count} خلايا بحالة تحذير قبل هذا التغيير.',
  'readiness.alreadyAmber.many': 'كانت {count} خلية بحالة تحذير قبل هذا التغيير.',
  'readiness.alreadyAmber.other': 'كانت {count} خلية بحالة تحذير قبل هذا التغيير.',
  'readiness.goAmber.zero': 'لا تتحوّل أي خلية إلى حالة تحذير.',
  'readiness.goAmber.one':
    'تتحوّل خلية واحدة إلى حالة تحذير — ستولّد تلك الشخصية النمطية أداة قصيرة، أو تفشل في التوليد أصلًا.',
  'readiness.goAmber.two':
    'تتحوّل خليتان إلى حالة تحذير — ستولّد تلك الشخصيات النمطية أداة قصيرة، أو تفشل في التوليد أصلًا.',
  'readiness.goAmber.few':
    'تتحوّل {count} خلايا إلى حالة تحذير — ستولّد تلك الشخصيات النمطية أداة قصيرة، أو تفشل في التوليد أصلًا.',
  'readiness.goAmber.many':
    'تتحوّل {count} خلية إلى حالة تحذير — ستولّد تلك الشخصيات النمطية أداة قصيرة، أو تفشل في التوليد أصلًا.',
  'readiness.goAmber.other':
    'تتحوّل {count} خلية إلى حالة تحذير — ستولّد تلك الشخصيات النمطية أداة قصيرة، أو تفشل في التوليد أصلًا.',
  'readiness.stranded': 'لن يعود بإمكان هذه الوحدة تقدير {stranded} من {checked} سؤالًا.',
  'readiness.strandedCertified.zero': 'لا شيء منها معتمد.',
  'readiness.strandedCertified.one': 'واحد منها معتمد، فيغادر مخزون السحب.',
  'readiness.strandedCertified.two': 'اثنان منها معتمدان، فيغادران مخزون السحب.',
  'readiness.strandedCertified.few': '{count} منها معتمدة، فتغادر مخزون السحب.',
  'readiness.strandedCertified.many': '{count} منها معتمد، فيغادر مخزون السحب.',
  'readiness.strandedCertified.other': '{count} منها معتمد، فيغادر مخزون السحب.',
  'readiness.nothingDeleted':
    'لا شيء يُحذف. تبقى في البنك وتبقى قابلة للقراءة؛ إنما تتوقّف عن كونها شيئًا تستطيع هذه الوحدة تقديمه.',
  'readiness.reason.type_unsupported': 'النوع لم يعد مدعومًا',
  'readiness.reason.dimension_dropped': 'البُعد أُسقط من الإعداد',
  'readiness.truncated': '…و{more} غيرها. القائمة مقتطعة للعرض؛ أما العدد أعلاه فليس كذلك.',

  /* -------------------------------------------------------------- change log */
  'changeLog.checking': 'جارٍ التحقّق من السلسلة…',
  'changeLog.chainUnavailable': 'حالة السلسلة غير متاحة',
  'changeLog.verified':
    'تم التحقّق من {count} مدخلًا — ما زال كل صف يعطي البصمة نفسها التي أعطاها حين كُتب',
  'changeLog.broken': 'انقطعت السلسلة عند المدخل {at}: {reason}',
  'changeLog.column.when': 'متى',
  'changeLog.column.what': 'ماذا',
  'changeLog.column.who': 'من',
  'changeLog.column.severity': 'الخطورة',
  'changeLog.filterByCategory': 'تصفية حسب الفئة',
  'changeLog.filterBySeverity': 'تصفية حسب الخطورة',
  'changeLog.filterByWho': 'تصفية حسب الفاعل…',
  'changeLog.filterByActor': 'تصفية حسب الفاعل',
  'changeLog.allCategories': 'كل الفئات',
  'changeLog.allSeverities': 'كل مستويات الخطورة',
  'changeLog.empty.title': 'لم يُسجَّل شيء بعد',
  'changeLog.empty.description':
    'كل إجراء يعدّل شيئًا يكتب مدخلًا واحدًا هنا. والسجل الفارغ يعني أنه لم يُغيَّر شيء — لا أن التسجيل معطّل.',
  'changeLog.category.Auth': 'المصادقة',
  'changeLog.category.Design': 'التصميم',
  'changeLog.category.Delivery': 'التقديم',
  'changeLog.category.Scoring': 'التقدير',
  'changeLog.category.Integration': 'التكامل',
  'changeLog.category.System': 'النظام',
  'changeLog.severity.info': 'معلومة',
  'changeLog.severity.notice': 'تنبيه',
  'changeLog.severity.warning': 'تحذير',
  'changeLog.severity.critical': 'حرِج',

  /* ---------------------------------------------------------------- settings */
  'settings.title': 'الإعدادات والإدارة',
  'settings.description':
    'مظهر الواجهة والحسابات وصلاحيات الأدوار وبيانات اعتماد التكامل ومسار التدقيق.',
  'settings.tab.appearance': 'المظهر',
  'settings.tab.users': 'مستخدمو الواجهة',
  'settings.tab.catalogue': 'الفهرس',
  'settings.tab.rbac': 'صلاحيات الأدوار',
  'settings.tab.apiKeys': 'مفاتيح الواجهة البرمجية',
  'settings.tab.webhooks': 'خطافات الويب',
  'settings.tab.changelog': 'سجل التغييرات',
  'settings.tab.audit': 'نقاط التكامل',
  'settings.noRecords': 'لا توجد سجلات',
  'settings.appearance.description':
    'تحدّد السمة سلّم الرموز كاملًا — اللوحة اللونية واستدارة الزوايا والخط — فتتبعها كل شاشة ومخطط وحوار. تُطبَّق فورًا وتُحفَظ على هذا الجهاز.',
  'settings.theme.hint': 'Ocean Breeze هي الافتراضية — اللوحة التي عُرضت بها الواجهة.',
  'settings.theme.matchSystem': 'مطابقة النظام',
  'settings.mode.hint':
    'مستقل عن السمة — كل سمة تأتي بلوحة فاتحة وأخرى داكنة. متاح أيضًا من الأيقونة في الشريط العلوي.',
  'settings.changelog.description':
    'من غيّر ماذا ومتى. كل إجراء يعدّل شيئًا يكتب مدخلًا واحدًا بالضبط، ولا شيء في المنصة يستطيع تعديله أو حذفه.',
  'settings.users.description':
    'من يستطيع تسجيل الدخول، وما الذي يحقّ له فعله. المحللون يقرؤون، والمسؤولون يؤلّفون ويشغّلون.',
  'settings.catalogue.title': 'نقل الفهرس بين البيئات',
  'settings.catalogue.description':
    'انقل فهرسًا بين البيئات. إجراء على مستوى البيئة لا على مستوى التأليف — فالاستيراد يعيد كتابة الفهرس الذي أُنتجت تحته كل نتيجة هنا.',
  'settings.rbac.title': 'صلاحيات الأدوار (RBAC)',
  'settings.rbac.description':
    'ما يستطيع كل دور فعله. تُقرأ من الواجهة البرمجية ولا تُكتب في هذه الشاشة — وهي الخريطة نفسها التي يتحقق منها الخادم في كل طلب.',
  'settings.rbac.note':
    'مُطبَّقة على الخادم في كل طلب. عرض النتائج والتحليلات غير مدرج لأنه ليس صلاحية: كل مشغّل مسجَّل الدخول يقرأ.',
  'settings.column.capability': 'الصلاحية',
  'capability.items:author': 'تأليف الأسئلة واعتمادها',
  'capability.design:draft': 'تعديل مسودات الوحدات والشخصيات، بما فيها إعداد التقدير',
  'capability.catalogue:publish': 'نشر الوحدات ومخططات التقييم',
  'capability.assessments:run': 'تشغيل التقييمات وإعادة تقديرها',
  'capability.qc:release': 'رفع تعليق ضبط الجودة',
  'capability.platform:manage': 'إدارة المستخدمين ومفاتيح الواجهة البرمجية',
  'permission.requiresRole': 'يتطلب دور {roles}',
  'settings.keys.title': 'كيف تُخزَّن المفاتيح',
  'settings.keys.storage':
    'لا يُحفَظ سوى بصمة تجزئة لكل مفتاح. تظهر القيمة الكاملة مرة واحدة عند الإصدار ولا يمكن استرجاعها بعدها — والتدوير يصدر مفتاحًا جديدًا بدل كشف القديم.',
  'settings.keys.scopes':
    'تُقيَّد المفاتيح بنطاقات لكل مجموعة مسارات (assessments:write و results:read و reference:read)، وكل إصدار وتدوير وإبطال يكتب حدث تدقيق.',
  'settings.apiKeys.description':
    'مسار الاعتماد الاحتياطي؛ ويُفضَّل OAuth2 client-credentials لأنظمة الجهات المستهلكة.',
  'settings.issueKey': 'إصدار مفتاح',
  'settings.apiKeys.empty.title': 'لم تُصدَر مفاتيح بعد',
  'settings.apiKeys.empty.description':
    'سجّل نظامًا مستهلكًا، ثم أصدر له مفتاحًا مقيَّد النطاق. تظهر قيمة المفتاح كاملةً مرة واحدة عند الإصدار، ولا يُحفظ منها سوى بصمة التجزئة.',

  /* ------------------------------------ integration consumers & keys (X3) */
  'settings.consumers.title': 'الأنظمة المستهلكة',
  'settings.consumers.register': 'تسجيل نظام مستهلك',
  'settings.consumers.empty.title': 'لا توجد أنظمة مستهلكة مسجّلة',
  'settings.consumers.empty.description':
    'النظام المستهلك هو كل ما يستدعي ‎/v1‎ — منصة نافس أو بيئة اختبار لجهة تكامل. سجّل نظامًا قبل إصدار أي مفتاح، فالمفتاح يخصّ نظامًا لا شخصًا.',
  'settings.consumers.column.consumer': 'النظام المستهلك',
  'settings.consumers.column.webhook': 'خطّاف الويب',
  'settings.consumers.column.keys': 'المفاتيح',
  'settings.consumers.keyCount': '{active} فعّال من {total}',
  'settings.consumers.webhookConfigured': 'موقَّع',
  'settings.consumers.webhookNone': 'بلا',
  'settings.consumers.status.active': 'نشِط',
  'settings.consumers.status.inactive': 'غير نشِط',
  'settings.consumers.deactivate': 'تعطيل',
  'settings.consumers.reactivate': 'إعادة التفعيل',
  'settings.consumers.updated': 'حُدِّث النظام المستهلك',
  'settings.consumers.deactivated.detail':
    'تفشل الآن كل مفاتيحه. ويبقى السجل — فالتعطيل ليس حذفًا.',
  'settings.consumers.reactivated.detail': 'عادت مفاتيحه إلى المصادقة من جديد.',
  'settings.consumers.updateFailed': 'تعذّر تحديث النظام المستهلك',
  'settings.consumers.new.title': 'تسجيل نظام مستهلك',
  'settings.consumers.new.description':
    'الاسم هو ما يظهر به هذا النظام في مسار التدقيق. وخطّاف الويب اختياري — غير أن رابطًا بلا مفتاح توقيع يعني تسليمًا غير موقَّع، وهذا ما لا ترسله المنصة، فلذلك يسير الاثنان معًا.',
  'settings.consumers.name': 'الاسم',
  'settings.consumers.namePlaceholder': 'تكامل نافس',
  'settings.consumers.webhookUrl': 'رابط خطّاف الويب (اختياري)',
  'settings.consumers.webhookSecret': 'مفتاح التوقيع',
  'settings.consumers.webhookSecret.hint':
    'ستة عشر محرفًا على الأقل. يُحفظ ولا يُعرض مرة أخرى، وتُوقَّع به كل عملية تسليم بخوارزمية HMAC-SHA256.',
  'settings.consumers.secretRequired':
    'مفتاح التوقيع مطلوب متى ضُبط رابط لخطّاف الويب — فعمليات التسليم موقَّعة.',
  'settings.consumers.secretTooShort': 'يجب ألا يقلّ مفتاح التوقيع عن ستة عشر محرفًا.',
  'settings.consumers.nameRequired': 'لا بدّ للنظام المستهلك من اسم — فهو ما يسجّله مسار التدقيق.',
  'settings.consumers.create': 'تسجيل',
  'settings.consumers.creating': 'جارٍ التسجيل…',
  'settings.consumers.created': 'سُجِّل النظام المستهلك',
  'settings.consumers.createFailed': 'تعذّر تسجيل النظام المستهلك',

  'settings.keys.issuedKeys': 'المفاتيح المُصدَرة',
  'settings.keys.column.rotated': 'آخر تدوير',
  'settings.keys.status.active': 'فعّال',
  'settings.keys.status.revoked': 'مُبطَل',
  'settings.keys.never': 'لم يحدث',
  'settings.keys.issue.title': 'إصدار مفتاح لـ{consumer}',
  'settings.keys.issue.description':
    'يظهر المفتاح في الشاشة التالية ولا يظهر في غيرها أبدًا. ولا يُحفظ منه سوى بصمة تجزئة، فلا شاشة ولا طلب دعم يستطيع عرضه عليك مرة ثانية.',
  'settings.keys.label': 'التسمية',
  'settings.keys.labelPlaceholder': 'نافس — بيئة الإنتاج',
  'settings.keys.labelHint':
    'الغرض من هذا الاعتماد. وهي الشيء الوحيد الذي يميّز مفتاحًا عن آخر في مسار التدقيق.',
  'settings.keys.scopes.legend': 'النطاقات',
  'settings.keys.scopeRequired': 'اختر نطاقًا واحدًا على الأقل — فالمفتاح بلا نطاق لا يستدعي شيئًا.',
  'settings.keys.issuing': 'جارٍ الإصدار…',
  'settings.keys.issueFailed': 'تعذّر إصدار المفتاح',
  'settings.keys.issued.title': 'صدر المفتاح',
  'settings.keys.issued.notice':
    'هذه هي المرة الوحيدة التي يُعرض فيها هذا المفتاح. انسخه الآن إلى مخزن أسرار النظام المستهلك — فلا يُحفظ هنا سوى بصمته، ولا سبيل إلى استرجاعه. والمفتاح الضائع يُدوَّر ولا يُستعاد.',
  'settings.keys.copyKey': 'نسخ المفتاح',
  'settings.keys.rotate': 'تدوير',
  'settings.keys.rotate.title': 'هل تريد تدوير «{label}»؟',
  'settings.keys.rotate.description':
    'يُصدَر سرّ جديد ويُعرض مرة واحدة. ويتوقف السرّ الحالي عن العمل فورًا دون مهلة سماح، فلا بدّ من تحديث النظام المستهلك قبل استدعائه التالي.',
  'settings.keys.rotating': 'جارٍ التدوير…',
  'settings.keys.rotated.title': 'دُوِّر المفتاح',
  'settings.keys.rotated.notice':
    'هذه هي المرة الوحيدة التي يُعرض فيها المفتاح الجديد، ولا يمكن استرجاعه بعدها. وقد توقّف السرّ السابق عن العمل لحظة إصدار هذا.',
  'settings.keys.rotateFailed': 'تعذّر تدوير المفتاح',
  'settings.keys.revoke': 'إبطال',
  'settings.keys.revoke.title': 'هل تريد إبطال «{label}»؟',
  'settings.keys.revoke.description':
    'يتوقف الاعتماد عن العمل فورًا دون مهلة سماح — فالسبب المعتاد للإبطال أن المفتاح صار بيد غيرك.',
  'settings.keys.revoke.recordKept':
    'ويبقى السجل ولا يُحذف: فمعرفة أيّ مفتاح أجرى أيّ استدعاء تبقى بعد زوال المفتاح، ومسار التدقيق بدونها غير مقروء.',
  'settings.keys.revoking': 'جارٍ الإبطال…',
  'settings.keys.revoked': 'أُبطل المفتاح',
  'settings.keys.revokeFailed': 'تعذّر إبطال المفتاح',
  'settings.scope.assessments:write': 'تسجيل المرشحين وبدء الجلسات وإرسال الإجابات',
  'settings.scope.results:read': 'قراءة النتائج وحالة المرشح ومغلّفات الدرجات',
  'settings.scope.reference:read': 'قراءة فهرس الوحدات والشخصيات النمطية والخطط',
  'settings.webhooks.description':
    'تسليم صادر بضمان مرة واحدة على الأقل، وتراجع أُسّي، وطابور رسائل ميتة.',
  'settings.webhooks.empty.title': 'لا توجد نقاط خطافات ويب مسجّلة',
  'settings.webhooks.empty.description':
    'تسجّل الجهة المستهلكة رابطها ومفتاح التوقيع هنا. وتُوقَّع عمليات التسليم بـHMAC-SHA256 ليتحقق المستقبِل من صحتها.',
  'settings.audit.title': 'نقاط التكامل والتدقيق',
  'settings.audit.description':
    'إلحاقي فقط ومسلسل بالتجزئة — كل صف يحمل بصمة سابقه، فيصبح العبث قابلًا للكشف.',
  'settings.audit.empty.title': 'لا توجد أحداث تدقيق بعد',
  'settings.audit.empty.description':
    'كل إجراء إداري يعدّل شيئًا يكتب صف تدقيق واحدًا بالضبط عبر مُصدِر الأحداث المشترك، فلا يمكن للمسار أن ينحرف عما حدث فعلًا.',
  'settings.column.label': 'التسمية',
  'settings.column.key': 'المفتاح',
  'settings.column.scopes': 'النطاقات',
  'settings.column.created': 'أُنشئ في',
  'settings.column.endpoint': 'نقطة النهاية',
  'settings.column.event': 'الحدث',
  'settings.column.delivery': 'التسليم',
  'settings.column.timestamp': 'الطابع الزمني (بتوقيت الخليج)',
  'settings.column.actor': 'الفاعل',
  'settings.column.category': 'الفئة',
  'settings.column.sourceIp': 'عنوان IP المصدر',

  /* ---------------------------------------------------------- notifications */
  'notifications.description':
    'أحداث المنصة التي تستدعي الانتباه — إخفاقات التسليم ومؤشرات الجودة وطلبات الوصول وتغييرات التصميم.',
  'notifications.markAllRead': 'تعليم الكل كمقروء',
  'notifications.allMarkedRead': 'عُلّمت كل الإشعارات كمقروءة',
  'notifications.settings': 'إعدادات الإشعارات',
  'notifications.search': 'البحث في الإشعارات',
  'notifications.searchPlaceholder': 'ابحث في الإشعارات…',
  'notifications.tab.all': 'الكل ({count})',
  'notifications.tab.unread': 'غير المقروءة ({count})',
  'notifications.noMatches': 'لا شيء مطابق',
  'notifications.noMatches.description': 'لا يوجد إشعار يطابق البحث أو المرشِّح الحالي.',
  'notifications.none': 'لا توجد إشعارات',
  'notifications.none.description':
    'ستظهر هنا مؤشرات ضبط الجودة وإخفاقات تسليم الإشعارات وأخطاء الاستيراد بمجرد تشغيل تلك الأنظمة.',
  'notifications.markRead': 'تعليم كمقروء',
  'notifications.accepted': 'مقبول · مُسجَّل في هذه الجلسة فقط',
  'notifications.declined': 'مرفوض · مُسجَّل في هذه الجلسة فقط',
  'notifications.footnote':
    'هذه أحداث توضيحية. تصبح التغذية حيّة عند وجود واجهة قراءة على مسار التدقيق — الأحداث نفسها المسلسلة بالتجزئة التي ستُطلق إشعارات result.finalized.',
  'notifications.kind.result': 'نتيجة',
  'notifications.kind.webhook': 'تسليم',
  'notifications.kind.quality': 'جودة',
  'notifications.kind.plan': 'تصميم',
  'notifications.kind.access': 'وصول',

  /* ------------------------------------------------- personas and the matrix */
  'matrix.title': 'الشخصيات النمطية والمصفوفة',
  'matrix.description':
    'كل فئة جمهور، وكم سؤالًا تتلقّاه من كل وحدة — مُتحقَّق منه مباشرةً مقابل البنك المعتمد.',
  'matrix.amber.zero': 'لا توجد خلايا بحالة تحذير',
  'matrix.amber.one': 'خلية واحدة بحالة تحذير',
  'matrix.amber.two': 'خليتان بحالة تحذير',
  'matrix.amber.few': '{count} خلايا بحالة تحذير',
  'matrix.amber.many': '{count} خلية بحالة تحذير',
  'matrix.amber.other': '{count} خلية بحالة تحذير',
  'matrix.allCovered': 'كل الخلايا مغطّاة',
  'matrix.loadFailed': 'تعذّر تحميل المصفوفة',
  'matrix.noCell': 'لا توجد خلية',
  'matrix.items.zero': 'لا توجد أسئلة',
  'matrix.items.one': 'سؤال واحد',
  'matrix.items.two': 'سؤالان',
  'matrix.items.few': '{count} أسئلة',
  'matrix.items.many': '{count} سؤالًا',
  'matrix.items.other': '{count} سؤال',
  'matrix.available': '{count} متاح',
  'matrix.provenance':
    'التغطية تجميع واحد للشبكة كلها لا استعلام لكل خلية، وهي تحكم على الأهلية بالطريقة نفسها التي يحكم بها التوليد — فالخلية التي يستبعد استعلام مخزونها معظم البنك تظهر بحالة تحذير هنا بدل أن تفشل مع أول مرشّح.',
  'matrix.cell.description': 'كم سؤالًا تتلقّى فئة الجمهور هذه من هذه الوحدة، وكيف تُسحَب.',
  'matrix.itemCount': 'عدد الأسئلة',
  'matrix.itemCount.detail':
    'كم سؤالًا تُطرح على فئة الجمهور هذه من هذه الوحدة. حتى 60 سؤالًا، ليبقى التقييم بطول معقول.',
  'matrix.shuffle': 'خلط ترتيب العرض لكل مرشّح',
  'matrix.balance': 'موازنة السحب عبر الأبعاد',
  'matrix.poolSummary': '{available} سؤالًا معتمدًا يطابق مخزون هذه الخلية؛ والمطلوب {requested}.',
  'matrix.willRefuse': 'سيرفض التوليد هذه الخلية حتى يكبر البنك أو ينخفض العدد.',
  'matrix.dimensionBalance': '{dimension}: {available} متاح، {needed} مطلوب',
  'matrix.saveCell': 'حفظ الخلية',
  'matrix.cellSaved': 'حُفظت الخلية',
  'matrix.cellSaved.ok': '{available} سؤالًا معتمدًا متاحًا مقابل {requested}.',
  'matrix.cellSaved.amber': 'تحذير: {available} سؤالًا معتمدًا فقط مقابل {requested} مطلوبًا.',
  'matrix.cellSaveFailed': 'تعذّر حفظ الخلية',

  'personas.new': 'شخصية نمطية جديدة',
  'personas.empty.title': 'لا توجد شخصيات نمطية مُعرَّفة',
  'personas.empty.description':
    'تحمل الشخصية النمطية خطة من وحدات مرتّبة. ثم تقرّر المصفوفة كم سؤالًا تتلقّى كل فئة جمهور.',
  'personas.empty.action': 'أنشئ الشخصية النمطية الأولى',
  'personas.editPlan': 'تعديل الخطة',
  'personas.draftPending': 'مسودة معلّقة',
  'personas.active': 'نشِطة',
  'personas.retiredState': 'موقوفة',
  'personas.activeHint': 'يمكن بدء تقييمات جديدة لفئة الجمهور هذه.',
  'personas.retiredHint':
    'لا تبدأ شيئًا جديدًا. تبقى النتائج القائمة قابلة للقراءة وما زال المعرّف يُحَل.',
  'personas.action.retire': 'إيقاف — منع التقييمات الجديدة، بشكل قابل للتراجع',
  'personas.action.reactivate': 'إعادة التفعيل — إتاحة فئة الجمهور هذه مجددًا',
  'personas.retired': '«{persona}» أُوقفت',
  'personas.retired.detail.zero':
    'لا يمكن بدء أي تقييم جديد لفئة الجمهور هذه. ولا توجد تقييمات مكتملة تسمّيها.',
  'personas.retired.detail.one':
    'لا يمكن بدء أي تقييم جديد لفئة الجمهور هذه. ما زال تقييم مكتمل واحد يسمّيها ويبقى قابلًا للقراءة — المعرّف لا يختفي.',
  'personas.retired.detail.two':
    'لا يمكن بدء أي تقييم جديد لفئة الجمهور هذه. ما زال تقييمان مكتملان يسمّيانها ويبقيان قابلين للقراءة — المعرّف لا يختفي.',
  'personas.retired.detail.few':
    'لا يمكن بدء أي تقييم جديد لفئة الجمهور هذه. ما زالت {count} تقييمات مكتملة تسمّيها وتبقى قابلة للقراءة — المعرّف لا يختفي.',
  'personas.retired.detail.many':
    'لا يمكن بدء أي تقييم جديد لفئة الجمهور هذه. ما زال {count} تقييمًا مكتملًا يسمّيها ويبقى قابلًا للقراءة — المعرّف لا يختفي.',
  'personas.retired.detail.other':
    'لا يمكن بدء أي تقييم جديد لفئة الجمهور هذه. ما زال {count} تقييم مكتمل يسمّيها ويبقى قابلًا للقراءة — المعرّف لا يختفي.',
  'personas.reactivated': '«{persona}» أصبحت نشِطة مجددًا',
  'personas.reactivated.detail':
    'على الخطة {version} — التي غادرت عليها. عاينها إن كان الفهرس قد تغيّر تحتها.',
  'personas.reactivateFailed': 'تعذّرت إعادة التفعيل',

  /* -------------------------------------------------------------- analytics */
  'analytics.description':
    'ملامح المجموعات بحسب الشخصية النمطية والوحدة — القاعدة الاستدلالية لتحديث معايير التقدير.',
  'analytics.filterByPersona': 'تصفية حسب الشخصية النمطية',
  'analytics.allPersonas': 'كل الشخصيات النمطية',
  'analytics.loadFailed': 'تعذّر تحميل التحليلات',
  'analytics.empty.title': 'لا توجد نتائج مُقدَّرة لهذه المجموعة',
  'analytics.empty.description':
    'تمتلئ مخططات المجموعة بمجرد تقدير التقييمات. وسّع المرشِّحات، أو شغّل تقييمًا من التقديم ← إجراء تقييم.',
  'analytics.results.zero': 'لا توجد نتائج',
  'analytics.results.one': 'نتيجة واحدة',
  'analytics.results.two': 'نتيجتان',
  'analytics.results.few': '{count} نتائج',
  'analytics.results.many': '{count} نتيجة',
  'analytics.results.other': '{count} نتيجة',
  'analytics.dimensionProfile': 'ملمح الأبعاد',
  'analytics.dimensionProfile.hint':
    '{module} — متوسط الدرجة لكل بُعد في هذه المجموعة. وهذه هي الأرقام التي ينبغي تحديث معايير الشخصية النمطية منها.',
  'analytics.noDimensions': 'لا توجد أبعاد لهذا التحديد.',
  'analytics.funnel': 'مسار الإكمال',
  'analytics.funnel.hint': 'أين تقف نسخ التقييم في دورة الحياة، لهذه المجموعة.',
  'analytics.funnel.note':
    'لا تُصفَّى حالات نسخ التقييم بحسب الشخصية النمطية — فالمصدر هو تغذية نسخ التقييم، وهي تعدّ كل تقييم لا المُقدَّرة وحدها.',
  'analytics.column.instances': 'نسخ التقييم',
  'analytics.headlines': 'أكثر العناوين تكرارًا',
  'analytics.headlines.hint':
    'العنوان الذي أبلغت عنه كل نتيجة، الأكثر تكرارًا أولًا. في وحدة الميول هو رمز هولاند؛ وفي أي نموذج آخر هو ما يسمّيه ذلك النموذج عنوانًا — والشاشة لا تعرف أيّهما.',
  'analytics.noHeadlines': 'لا توجد عناوين لهذا التحديد.',
  'analytics.provenance':
    'مقروء من التجميع المفرد نفسه الذي تستخدمه لوحة المعلومات، مُصفّى بالشخصية النمطية والوحدة — بلا مسار ثانٍ وبلا $lookup.',

  /* ----------------------------------------------------------------- result */
  'result.eyebrow': 'التقديم · النتيجة',
  'result.loadFailed': 'تعذّر تحميل هذه النتيجة',
  'result.qcFlags': 'مؤشرات جودة الاستجابة:',
  'result.qcFlags.hint':
    'تصف هذه المؤشرات كيف مُلئ النموذج لا ما يقوله. فسّر الدرجات بحذر.',
  'result.reads': 'قراءتان لهذه النتيجة',
  'result.tab.interpretation': 'التفسير',
  'result.tab.technical': 'البيانات الفنية',
  'result.allDimensions': 'كل الأبعاد',
  'result.allDimensions.hint': 'الأرقام نفسها التي يرسمها المخطط أعلاه.',
  'result.narrative': 'ملاحظات التفسير',
  'result.narrative.hint': 'ما توصّلت إليه الأداة بشأن هذا الملف.',
  'result.narrative.pending':
    'لم تُصَغ عبارات هذه الملاحظات بعد، لذا يُعرض بدلًا منها النطاق الذي أسندته الأداة.',
  'result.pathways': 'المسارات المهنية المقترحة',
  'result.pathways.hint':
    'المهن التي يتوافق معها هذا الملف، من إصدار ESCO المختوم على هذه النتيجة.',
  'result.pathways.fit': 'التوافق {fit}',
  'result.dimension': 'البُعد',
  'result.score': 'الدرجة',
  'result.band': 'النطاق',
  /* المجموعة المرجعية (N3): تُذكر مرة واحدة أسفل الجدول، لأنها خاصية جدول
     المعايير لا خاصية بُعدٍ بعينه. */
  'result.percentile': 'المئين',
  'result.percentile.short': 'المئين {value}',
  'result.percentile.cohort': 'يقارن المئين هذا المرشّح بمجموعة {cohort}.',
  'result.percentile.cohortUnnamed':
    'يقارن المئين هذا المرشّح بمجموعة المعايير المطبَّقة عند احتساب هذه النتيجة، وهذه النتيجة لا تسمّيها.',
  /* المدى (N4): يُسمّى بطريقة بنائه لا بـ«فاصل الثقة»، لأن هذه التسمية ادّعاء
     بتغطية إحصائية لا يحملها المغلّف، ومن يفترض 95% سيقرأ نطاق الخطأ المعياري
     ضِعف ما هو عليه. وإن ورد أساس لا يعرفه هذا الجدول، تُستخدم الصيغة العامة
     التي لا تسمّي طريقة بناء بدل إظهار قيمة الحقل كما هي. */
  'result.interval.header': 'المدى',
  'result.interval.header.standard_error': 'نطاق الخطأ المعياري',
  'result.interval.range': '{low} – {high}',
  'result.interval.hint':
    'يبيّن المدى دقة قياس هذه الدرجة، على مقياس 0–100 نفسه المستخدَم في الدرجة، ولا يذكر أي مستوى ثقة.',
  'result.interval.hint.standard_error':
    'يمتد المدى خطأً معياريًا واحدًا على جانبَي الدرجة، على مقياس 0–100 نفسه؛ فهو مؤشّر على دقة التقدير لا فاصل ثقة.',
  'result.precision': 'دقة التقدير',
  'result.precision.hint':
    'θ وخطؤها المعياري على مقياس اللوجيت الذي يُقدَّر عليه مستوى القدرة، لا على مقياس 0–100 المستخدَم في درجات الأبعاد.',
  'result.precision.theta': 'θ',
  'result.precision.se': 'SE(θ)',
  'result.provenance': 'مصدر البيانات',
  'result.provenance.hint': 'تُختم عند إنهاء النتيجة، ولا يُعاد حسابها عند القراءة أبدًا.',
  'result.field.result': 'النتيجة',
  'result.field.config': 'إعداد التقدير',
  'result.field.instrument': 'الأداة (الإضافة)',
  'result.field.administeredIn': 'قُدِّم بـ',
  'result.field.esco': 'إصدار ESCO',
  'result.field.supersedes': 'يَحلّ محل',
  'result.field.finalised': 'أُنهيت في',
  'result.field.narrativeKeys': 'مفاتيح التفسير',
  'result.firstScoring': '— أول تقدير',
  'result.rescore': 'إعادة التقدير',
  'result.rescoring': 'جارٍ إعادة التقدير…',
  'result.rescored': 'أُعيد التقدير',
  'result.rescored.detail':
    'نتيجة جديدة {result} بالإعداد إصدار {version}. النتيجة السابقة محفوظة.',
  'result.rescoreFailed': 'تعذّرت إعادة التقدير',

  /* -------------------------------------------------------------- QC review */
  'qcReview.description':
    'نتائج قُدِّرت ثم توقّفت. تُحجب درجاتها عن الجهة المستهلكة حتى يُفرج عنها أحد من هنا.',
  'qcReview.held.zero': 'لا شيء محجوز',
  'qcReview.held.one': 'محجوزة واحدة',
  'qcReview.held.two': 'محجوزتان',
  'qcReview.held.few': '{count} محجوزات',
  'qcReview.held.many': '{count} محجوزة',
  'qcReview.held.other': '{count} محجوزة',
  'qcReview.nothingHeld': 'لا شيء محجوز',
  'qcReview.column.candidate': 'المرشّح',
  'qcReview.column.why': 'سبب الحجز',
  'qcReview.column.scored': 'وقت التقدير',
  'qcReview.flagHint.straight_lining': 'كانت كل الإجابات الرقمية تقريبًا بالقيمة نفسها.',
  'qcReview.flagHint.speeding': 'كان وسيط زمن الاستجابة أقل من عتبة الوحدة.',
  'qcReview.reviewResult': 'مراجعة النتيجة',
  'qcReview.release': 'الإفراج',
  'qcReview.releasing': 'جارٍ الإفراج…',
  'qcReview.released': 'تم الإفراج',
  'qcReview.released.detail': 'اكتمل {result}، وتُقدَّم درجته إلى الجهة المستهلكة من الآن فصاعدًا.',
  'qcReview.releaseFailed': 'تعذّر الإفراج',

  /* ------------------------------------------------ القرار (V2) */
  'qcReview.release.title': 'الإفراج عن {result}',
  'qcReview.release.description':
    'الإفراج يُكمل التقييم ويبدأ تقديم درجته إلى الجهة المستهلكة. اذكر ما الذي راجعته، ليُقرأ القرار لاحقًا عند الاعتراض على الدرجة.',
  'qcReview.hold': 'إعادة الحجز',
  'qcReview.holding': 'جارٍ إعادة الحجز…',
  'qcReview.hold.title': 'إعادة حجز {result}',
  'qcReview.hold.description':
    'تتوقّف درجته عن الوصول إلى الجهة المستهلكة من جديد. هذا تراجع عن إفراج سابق ولا شيء أوسع — فالتقييم الذي اكتمل من تلقاء نفسه لم يُفرج عنه أصلًا ولا يمكن حجزه.',
  'qcReview.heldAgain': 'أُعيد الحجز',
  'qcReview.heldAgain.detail': 'حُجبت درجة {result} عن الجهة المستهلكة مجددًا، وعاد إلى القائمة أدناه.',
  'qcReview.holdFailed': 'تعذّرت إعادة الحجز',
  'qcReview.reason.label': 'السبب',
  'qcReview.reason.placeholder':
    'مثال: نمط الإجابات يطابق وحدة سابقة للمرشّح نفسه، وزمن الاستجابة معقول لقارئ سريع.',
  'qcReview.reason.hint': '{min} حرفًا على الأقل — اذكر ما الذي راجعته، لا أنك راجعت.',
  'qcReview.reason.counter': '{count} من {min}',
  'qcReview.reason.tooShort':
    'قصير جدًا. يجب ألّا يقلّ السبب عن {min} حرفًا — فكلمة «تمّ» أو «روجعت» لا تسجّل شيئًا يُحاسَب عليه أحد لاحقًا.',
  'qcReview.reason.tooLong':
    'طويل جدًا. أبقِه دون {max} حرف؛ فتفاصيل الحالة مكانها النتيجة نفسها لا سجلّ تدقيق لا يقبل التعديل.',
  'qcReview.source.machine': 'حُجز آليًا',
  'qcReview.source.reviewer': 'أعاد حجزه مُراجِع',
  'qcReview.reviewerBy': '{actor} · {at}',
  'qcReview.releasedHere.title': 'أُفرج عنها للتو',
  'qcReview.releasedHere.description':
    'قرارات اتخذتها في هذه الشاشة. التقييم المُفرج عنه يغادر القائمة، وهنا يمكن إعادة حجزه — وهذه القائمة غير محفوظة وتُمحى عند مغادرة الشاشة.',
  'qcReview.empty.title': 'لا شيء ينتظر المراجعة',
  'qcReview.empty.description':
    'التقييم الذي ترفع إضافته مؤشر ضبط جودة يُحجز هنا بدل أن يكتمل، وتُحجب درجته عن واجهة التكامل حتى يُفرج عنه.',
  'qcReview.footnote':
    'المؤشر سبب للنظر لا حكم نهائي — فالمستجيب المتّسق فعلًا قد يعطي إجابات نمطية. ويسجّل الإفراجُ مَن قرّر، وأي المؤشرات قرّر رغمها؛ وهو في',
  'qcReview.footnote.link': 'الإعدادات ← سجل التغييرات',

  /* --------------------------------------------------------------- start run */
  'startRun.description':
    'أداة ضبط جودة داخلية — تولّد نسخة تقييم حقيقية وتُشغّلها عبر المسار الحقيقي.',
  'startRun.newRun': 'تقييم جديد',
  'startRun.resumeHint':
    'البدء مجددًا للمرشّح والوحدة نفسيهما يستأنف النسخة المفتوحة بدلًا من سحب نسخة جديدة.',
  'startRun.candidateRef': 'مرجع المرشّح',
  'startRun.candidateRef.detail':
    'أي مرجع تستخدمه لتعريف هذا المرشّح في مكان آخر. لا تحفظ المنصة أسماء ولا بيانات شخصية، بل هذا فقط.',
  'startRun.persona': 'الشخصية النمطية',
  'startRun.selectPersona': 'اختر فئة الجمهور',
  'startRun.selectModule': 'اختر وحدة',
  'startRun.module.detail':
    'الوحدة تحدّد الأسئلة المطروحة وطريقة تقديرها وكيفية عرض النتيجة.',
  'startRun.start': 'ابدأ',
  'startRun.generating': 'جارٍ التوليد…',
  'startRun.failed': 'تعذّر بدء التقييم',

  /* ------------------------------------------------------------- languages */
  'languageName.en-AE': 'الإنجليزية',
  'languageName.ar-AE': 'العربية',

  /* --------------------------------------------------------- take-test harness */
  'run.eyebrow': 'التقديم · إجراء اختبار',
  'run.description': 'أداة ضبط جودة داخلية — تُشغّل المسار الحقيقي من طرفه إلى طرفه.',
  // Phrased as a label rather than "بـ{language}": an Arabic preposition
  // attaches to the following word, and gluing a particle onto an interpolated
  // variable produces "بـالإنجليزية" — readable, but visibly machine-made. This
  // is the commonest way a translated UI gives itself away.
  'run.administeredIn': 'لغة التقديم: {language}',
  'run.questionOf': 'السؤال {n} من {total}',
  'run.percentAnswered': 'تمت الإجابة عن %{progress}',
  'run.back': 'رجوع',
  'run.next': 'التالي',
  'run.finish': 'إنهاء',
  'run.reviewAction': 'مراجعة',
  'run.review.title': 'راجع إجاباتك',
  'run.review.description': 'تحقّق مما تخطّيته قبل التسليم.',
  'run.answeredOf': '{answered} من {total}',
  'run.unanswered': 'بلا إجابة',
  'run.keepAnswering': 'متابعة الإجابة',
  'run.submit': 'تسليم',
  'run.scoring': 'جارٍ التقدير…',
  'run.autosave':
    'تُحفظ الإجابات أولًا بأول. يمكنك إغلاق هذه الصفحة والعودة إلى الرابط نفسه — يُستعاد التقدّم من الخادم لا من هذا المتصفح.',
  'run.rankingHint': 'اضغط بحسب الأفضلية — الأكثر تفضيلًا أولًا.',
  'run.slider.unanswered': 'اسحب للإجابة',
  'run.submitted': 'سُلّم وقُدِّر',
  'run.submitted.detail': 'النتيجة {result}',
  'run.submitFailed': 'تعذّر التسليم',
  'run.incomplete': 'بعض الأسئلة بلا إجابة',
  'run.incomplete.detail': 'بقي {count} للإجابة — مُميَّزة في قائمة المراجعة.',
  'run.loadFailed': 'تعذّر تحميل هذا التقييم',
  'run.expired': 'انتهت صلاحية هذا التقييم. تنتهي صلاحية النسخ بعد 14 يومًا من عدم النشاط.',
  'run.noItems': 'لا توجد أسئلة في هذا التقييم',
  'run.noItems.description': 'لم يُسلَّم أي شيء.',
  'run.noRenderer': 'لا يوجد عارض لنوع السؤال {type}. أضِف واحدًا إلى RENDERERS.',

  /* --------------------------------------------- M2: التوزّعات لا المتوسّطات */
  'distribution.title': 'التشتّت حسب البُعد',
  'distribution.hint':
    'المتوسّط وحده لا يُظهر بُعدًا ثنائي المنوال — تجمّعان متباعدان ووسط خالٍ يعطيان الرقم نفسه الذي يعطيه تشابه الجميع.',
  'distribution.pick': 'البُعد',
  'distribution.none': 'لا يوجد بُعد في هذه الوحدة له توزّع بعد.',
  'distribution.missing':
    'لم تُرجِع الواجهة أي توزّع لهذا البُعد. المتوسّط أعلاه هو كل ما أرسلته.',
  'distribution.candidates.zero': 'لا يوجد مرشّحون',
  'distribution.candidates.one': 'مرشّح واحد',
  'distribution.candidates.two': 'مرشّحان',
  'distribution.candidates.few': '{count} مرشّحين',
  'distribution.candidates.many': '{count} مرشّحًا',
  'distribution.candidates.other': '{count} مرشّح',
  'distribution.mean': 'المتوسّط',
  'distribution.sd': 'الانحراف المعياري',
  'distribution.sd.hint':
    'انحراف معياري للعيّنة (n−1) — فهذه النتائج عيّنة ممّن سيؤدّون الأداة لا المجتمع كلّه.',
  'distribution.range': 'المدى',
  'distribution.rangeValue': 'من {min} إلى {max}',
  'distribution.insufficient.title': 'مَعدود لا موصوف',
  'distribution.insufficient.body':
    '{n} من أصل {minimum} مرشّحًا يحتاجها التقدير. العدد حقيقي ومعروض؛ أمّا الشكل فمحجوب، لأن تشتّتًا محسوبًا على حفنة من الأشخاص يصف من أدّى الاختبار مصادفةً لا يصف البُعد نفسه.',
  'distribution.histogram.title': 'عدد المرشّحين في كل نطاق درجات',
  'distribution.bar': 'المرشّحون',
  'distribution.column.band': 'نطاق الدرجات',
  'distribution.column.candidates': 'المرشّحون',
  'distribution.bin': '{from}–{to}',
  'distribution.rescored.title': 'أُعيد تقدير هذه المجموعة',
  'distribution.rescored.body':
    'يُحتسب المتوسّط على {results} نتيجة بينما لا يقف خلفها سوى {candidates} مرشّح، أي أن الجلسة المُعاد تقديرها تُحتسب أكثر من مرّة فيه. أمّا التشتّت أدناه فيحتسب كل مرشّح مرّة واحدة. هذا خلل معروف في المتوسّط، ويُعرض هنا بدل تصحيحه بصمت، لأن الرقمين يجيبان عن سؤالين مختلفين.',

  /* ------------------------------------------------------- M1: تحليل الأسئلة */
  'nav.itemAnalysis': 'تحليل الأسئلة',
  'itemAnalysis.title': 'تحليل الأسئلة',
  'itemAnalysis.description':
    'ما الذي فعله بنك الأسئلة فعلًا حين واجهه المرشّحون — الدليل الذي يقف خلف اعتماد السؤال.',
  'itemAnalysis.pickModule.title': 'اختر وحدة',
  'itemAnalysis.pickModule.description':
    'تُحسب إحصاءات الأسئلة لكل وحدة على حدة: قيمة p من أداة ومتوسّط استجابة من أداة أخرى لا يشتركان في مقياس ولا يجتمعان في شاشة واحدة.',
  'itemAnalysis.includeHeld': 'تضمين الجلسات المحجوزة لضبط الجودة',
  'itemAnalysis.includeHeld.hint':
    'الجلسات المحجوزة داخلة في المجتمع افتراضيًا. إيقافها يعطي التحليل المُنقّى — وعندها يُحجب معدّل الحجز بدل عرضه صفرًا.',
  'itemAnalysis.runs.zero': 'لا توجد جلسات مُقدَّرة',
  'itemAnalysis.runs.one': 'جلسة مُقدَّرة واحدة',
  'itemAnalysis.runs.two': 'جلستان مُقدَّرتان',
  'itemAnalysis.runs.few': '{count} جلسات مُقدَّرة',
  'itemAnalysis.runs.many': '{count} جلسة مُقدَّرة',
  'itemAnalysis.runs.other': '{count} جلسة مُقدَّرة',
  'itemAnalysis.items.zero': 'لا توجد أسئلة',
  'itemAnalysis.items.one': 'سؤال واحد',
  'itemAnalysis.items.two': 'سؤالان',
  'itemAnalysis.items.few': '{count} أسئلة',
  'itemAnalysis.items.many': '{count} سؤالًا',
  'itemAnalysis.items.other': '{count} سؤال',
  'itemAnalysis.asOf': 'حتى {at}',
  'itemAnalysis.floor':
    'يحتاج التقدير إلى {minimum} إجابة. دون ذلك تبقى الأعداد ظاهرة ويُحجب التقدير: قيمة p محسوبة على ثلاث استجابات ضجيج مطبوع بالخط نفسه الذي تُطبع به قيمة محسوبة على ثلاثمائة.',
  'itemAnalysis.loadFailed': 'تعذّر تحميل تحليل الأسئلة',
  'itemAnalysis.empty.title': 'لم يُسلَّم شيء بعد',
  'itemAnalysis.empty.description':
    'لا توجد جلسات مُقدَّرة لهذه الوحدة ضمن التصفية الحالية، فلا شيء لتحليله. سلّمها إلى مرشّح وستظهر الإحصاءات هنا.',
  'itemAnalysis.column.item': 'السؤال',
  'itemAnalysis.column.responses': 'الإجابات',
  'itemAnalysis.column.central': 'مقياس النزعة المركزية',
  'itemAnalysis.column.correlation': 'ارتباط السؤال بالمجموع',
  'itemAnalysis.column.timing': 'الزمن على السؤال',
  'itemAnalysis.column.qcHold': 'معدّل حجز الجودة',
  'itemAnalysis.answeredOf': '{answered} من {delivered} مُسلَّمة',
  'itemAnalysis.scale': 'على {min}–{max}',
  'itemAnalysis.sd': 'انحراف {sd}',
  'itemAnalysis.correlation.hint':
    'مُصحَّح: السؤال مقابل مجموع بقية الأسئلة القابلة للتقدير في الجلسة، لا مقابل مجموع يضمّه هو نفسه.',
  'itemAnalysis.timing.quartiles': 'الربيع الأدنى {p25} · الربيع الأعلى {p75}',
  'itemAnalysis.timing.none': 'لا توجد استجابات مُوقّتة',
  'itemAnalysis.qcHold.runs.zero': 'لا توجد جلسات محجوزة',
  'itemAnalysis.qcHold.runs.one': 'جلسة محجوزة واحدة',
  'itemAnalysis.qcHold.runs.two': 'جلستان محجوزتان',
  'itemAnalysis.qcHold.runs.few': '{count} جلسات محجوزة',
  'itemAnalysis.qcHold.runs.many': '{count} جلسة محجوزة',
  'itemAnalysis.qcHold.runs.other': '{count} جلسة محجوزة',
  'itemAnalysis.qcHold.excluded': 'الجلسات المحجوزة مستبعدة',
  'itemAnalysis.qcHold.excluded.hint':
    'ليس صفرًا. مع خروج الجلسات المحجوزة من المجتمع لا يوجد معدّل يُبلَّغ عنه — أعِد تشغيل المرشّح لرؤيته.',
  'itemAnalysis.detail.title': 'السؤال {item}',
  'itemAnalysis.detail.description': 'كيف توزّعت الاستجابات على هذا السؤال.',
  'itemAnalysis.detail.options': 'توزّع الخيارات',
  'itemAnalysis.detail.rankingNote':
    'في سؤال الترتيب يمثّل العدد كم مرّة وُضع الخيار أوّلًا. ومتوسّط الرتبة بجانبه هو ما لا يستطيع العدد وحده قوله: خيار يضعه الجميع ثانيًا لا يظهر في عدّ المرتبة الأولى إطلاقًا.',
  'itemAnalysis.column.option': 'الخيار',
  'itemAnalysis.column.chosen': 'اختير',
  'itemAnalysis.column.meanRank': 'متوسّط الرتبة',
  'itemAnalysis.openRow': 'افتح توزّع الاستجابات',

  'itemStatistic.p_value': 'قيمة p',
  'itemStatistic.p_value.hint':
    'نسبة من أجابوا إجابة صحيحة — مؤشّر الصعوبة الكلاسيكي.',
  'itemStatistic.mean_response': 'متوسّط الاستجابة',
  'itemStatistic.mean_response.hint':
    'متوسّط العلامة على مقياس السؤال نفسه، خامًا. لا يُطبَّق عكس المفتاح عمدًا: أثر السقف خاصيّة لما وضعه المرشّحون فعلًا.',
  'itemStatistic.mean_option_value': 'متوسّط قيمة الخيار',
  'itemStatistic.mean_option_value.hint':
    'متوسّط الفاعليّة المؤلَّفة للخيار المختار. مُتدرّج لا صواب/خطأ.',
  'itemStatistic.mean_anchor_level': 'متوسّط مستوى المرتكز',
  'itemStatistic.mean_anchor_level.hint': 'متوسّط المرتكز السلوكي المختار.',

  'absence.not_meaningful': 'غير مُعرَّف لهذا النوع',
  'absence.not_meaningful.hint':
    'دائم. لا يوجد تعريف قابل للدفاع عنه لهذه الإحصاءة في سؤال من نوع {type}، فلن يُنتجها أي قدر إضافي من البيانات.',
  'absence.insufficient': '{answered} من {minimum} إجابة',
  'absence.insufficient.hint':
    'محجوب حتى تتوفّر استجابات كافية للتقدير منها. مزيد من البيانات يعالج هذا.',
  'absence.no_variance': 'لا تباين يمكن قياسه',
  'absence.no_variance.hint':
    'الاستجابات كافية والإحصاءة مُعرَّفة لهذا النوع — لكن الجميع أجابوا الإجابة نفسها، فلم يبقَ ما يُحسب منه. وعرض صفر هنا يُقرأ كأنه نتيجة.',

  /* --------------------------------------------------- V1: دليل ضبط الجودة */
  'qcEvidence.action': 'الدليل',
  'qcEvidence.title': 'دليل النتيجة {result}',
  'qcEvidence.description':
    'ما الذي قاسه كل كاشف، والجلسة التي قاسه فيها. القرار يبقى قرار إنسان.',
  'qcEvidence.loadFailed': 'تعذّر تحميل الدليل',
  'qcEvidence.answeredOf': '{answered} من {items} مُجابة',
  'qcEvidence.scoredUnder': 'قُدِّرت وفق الإعداد v{version} · الأداة {instrument}',
  'qcEvidence.administeredIn':
    'أُجريت باللغة {language}. النصوص أدناه هي ما قرأه المرشّح، لا لغة واجهة الإدارة.',
  'qcEvidence.empty': 'لا أسئلة في هذه الجلسة، فلا نمط لعرضه.',

  'qcEvidence.verdict.title': 'ما الذي قاسه كل كاشف',
  'qcEvidence.fired': 'أُطلِق',
  'qcEvidence.didNotFire': 'لم يُطلَق',
  'qcEvidence.straightLining.title': 'الإجابة النمطية',
  'qcEvidence.straightLining.stat':
    '{proportion} من {n} سؤالًا كمّيًّا أُجيبت بالقيمة {value}، مقابل عتبة {threshold}',
  'qcEvidence.straightLining.belowMinimum':
    '{n} أسئلة كمّية، والمطلوب {minimum}. النسبة تبقى ملاحظة عن الجلسة، لكن الكاشف لا يُطلَق دون الحدّ الأدنى — فإجابتان متطابقتان من أصل اثنتين مصادفة لا نمط.',
  'qcEvidence.straightLining.nothingAnswered':
    'لم يُجَب أي سؤال كمّي برقم، فلا نسبة يمكن عرضها.',
  'qcEvidence.straightLining.denominator':
    'المقام هو الأسئلة الكمّية وحدها — فالجلسة التي تضمّ أيضًا أسئلة اختيار مُلزَم وترتيب أطول من هذا الرقم.',
  'qcEvidence.speeding.title': 'الإجابة المتسرّعة',
  'qcEvidence.speeding.stat':
    'الوسيط {median} عبر {n} استجابة مُوقّتة، مقابل عتبة {threshold}',
  'qcEvidence.speeding.untimed': 'لم تحمل أي استجابة زمنًا، فلا وسيط للمقارنة.',
  'qcEvidence.speeding.below':
    '{count} من {n} استجابة مُوقّتة وقعت دون العتبة. وصفي فقط — الوسيط هو ما يُطلق العلامة، فجلسة فيها أسئلة سريعة عدّة ووسيط بطيء ليست متسرّعة.',

  'qcEvidence.configGone.title': 'تعذّر عرض العتبات',
  'qcEvidence.configGone.body':
    'الإعداد الذي قُدِّرت به هذه الجلسة (v{version}) لم يعد محفوظًا، فلا شيء تُقارن الجلسة به. وعرض عتبات اليوم بدلًا منه يطبع قاعدة لم تُحاكَم بها هذه الجلسة قطّ. أمّا كل ما هو ملاحظة لا مقارنة — الإجابات والأزمنة والترتيب — فهو أدناه ولم يتأثّر.',

  'qcEvidence.flags.stored': 'مسجَّلة على النتيجة',
  'qcEvidence.flags.recomputed': 'القاعدة نفسها، مُعادة الآن',
  'qcEvidence.flags.none': 'لا شيء',
  'qcEvidence.flags.disagree.title': 'الاثنان لا يتّفقان',
  'qcEvidence.flags.disagree.body':
    'العلامات المسجّلة على النتيجة والعلامات التي تنتجها القاعدة نفسها الآن ليست المجموعة ذاتها. معنى ذلك أن الاستجابات أو الإعداد المحفوظ تغيّرا بعد تقدير هذه الجلسة. أبلِغ عن ذلك قبل اتّخاذ القرار — فهي نتيجة تخصّ المنصّة لا المرشّح.',

  'qcEvidence.pattern.title': 'نمط الاستجابة، بترتيب التسليم',
  'qcEvidence.pattern.hint':
    'نقطة لكل إجابة كمّية عند الموضع الذي واجهها فيه المرشّح. والفجوة سؤال بلا إجابة — وليست صفرًا أبدًا.',
  'qcEvidence.pattern.answer': 'إجابة كمّية',
  'qcEvidence.pattern.repeated': 'القيمة المتكرّرة ({value})',
  'qcEvidence.pattern.axisPosition': 'الموضع في الجلسة',
  'qcEvidence.pattern.axisValue': 'الإجابة',
  'qcEvidence.pattern.mixedScales':
    'هذه الأسئلة لا تشترك في مقياس واحد، فالارتفاعات غير قابلة للمقارنة بينها. وكل إجابة مذكورة مع مقياسها في الجدول أدناه.',
  'qcEvidence.pattern.noScalar':
    'لا شيء لرسمه: لم يُجَب أي سؤال كمّي في هذه الجلسة برقم. وكل الأسئلة في الجدول أدناه.',

  'qcEvidence.latency.title': 'الزمن على كل سؤال',
  'qcEvidence.latency.hint':
    'أجزاء الثانية لكل سؤال، بترتيب التسليم. والخط هو العتبة من الإعداد الذي قُدِّرت به الجلسة.',
  'qcEvidence.latency.normal': 'زمن الاستجابة',
  'qcEvidence.latency.below': 'دون العتبة',
  'qcEvidence.latency.threshold': 'العتبة',
  'qcEvidence.latency.none': 'لم تحمل أي استجابة في هذه الجلسة زمنًا.',
  'qcEvidence.latency.noThreshold':
    'لا خط عتبة: الإعداد الذي قُدِّرت به هذه الجلسة لم يعد محفوظًا، فلم تُجرَ أي مقارنة.',

  'qcEvidence.table.title': 'كل الأسئلة، بترتيب التسليم',
  'qcEvidence.column.position': '#',
  'qcEvidence.column.item': 'السؤال كما أُجري',
  'qcEvidence.column.answer': 'الإجابة',
  'qcEvidence.column.time': 'الزمن',
  'qcEvidence.column.key': 'مفتاح التقدير',
  'qcEvidence.unanswered': 'بلا إجابة',
  'qcEvidence.reverseKeyed': 'معكوس المفتاح',
  'qcEvidence.scalar': 'كمّي',
  'qcEvidence.ms': '{ms} م.ث',
  'qcEvidence.keyNote':
    'مفاتيح التقدير معروضة هنا لأنها هي الدليل: جلسة مسطّحة داخل بُعد واحد قد تكون مستجيبًا معقولًا، والجلسة نفسها عبر كل بُعد ليست كذلك.',

  /* ---------------------------------------------------------------- support */
  'support.description': 'أين تجد المساعدة بشأن واجهة الإدارة.',
  'support.body':
    'للمشكلات المتعلقة بالمنصة، تواصل مع مكتب خدمة ETCC. عند الإبلاغ عن مشكلة، أرفق معرّف التتبع (traceId) الظاهر في أي شاشة خطأ — فهو يربط بلاغك مباشرةً بسجلات الخادم ومسار التدقيق وأي إشعار صادر لذلك الإجراء.',
};
